'use strict';

const DEV_MOCK = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
const API_BASE = DEV_MOCK
  ? ''
  : location.hostname === 'games.muslimpro.com'
    ? 'https://partners-api.muslimpro.com/v1'
    : 'https://partners-api.staging.muslimpro.com/v1';
const STONES_PER_JAMRAH = 7;
const GRAVITY           = 9.8;
const EYE_H             = 1.6;
const FOCAL             = 700;

// Broad round stone pillar inside a sandstone basin (per references). Wide —
// it reads like a curved wall filling the view — but still a round cylinder.
// Physics uses the same ellipse so the hit test agrees with the visual.
const JAMARAT = {
  ula:   { name: 'Jamrat al-Ūlā',    ar: 'الجمرة الأولى',  short: 'Ūlā',    w: 4.5, d: 2.0, h: 4.8 },
  wusta: { name: 'Jamrat al-Wusṭā',  ar: 'الجمرة الوسطى', short: 'Wusṭā',  w: 5.0, d: 2.1, h: 5.1 },
  aqaba: { name: 'Jamrat al-ʿAqaba', ar: 'جمرة العقبة',    short: 'ʿAqaba', w: 5.5, d: 2.4, h: 5.4 },
};

const MODES = {
  nahr:     { stages: ['aqaba'] },
  tashreeq: { stages: ['ula', 'wusta', 'aqaba'] },
};

// Pillar silhouette — uniform round cylinder, full width all the way down.
// The basin's flat top ring is sized to match this exactly so the pillar's
// bottom edge IS the basin's inner edge (one curve, no separate ring).
function pillarRadiusFactor(yNorm) {
  return 1.5;
}

'use strict';

// Gamification API — Pattern D (cookie auth, credentials: 'include')
// All calls attach the HttpOnly session cookie automatically — no token in JS.
//
// Auth model: every backend endpoint requires a valid mp_session cookie.
// Guests play entirely client-side; no backend call is made until the user
// authenticates. At end of a guest game, the web triggers a native login via
// the bridge and then calls POST /game/session/submit in a single round-trip
// to create the session record and credit points atomically.
//
// DEV_MOCK (config.js) returns fake data on localhost; the dev fallback
// simulates a successful login a few hundred ms after auth:login_required.

const api = (() => {

  // --- dev mock state ---
  // Default is guest; ?signedIn=1 boots authenticated.
  // A simulated login flips _mockAuthed = true.

  let _mockBalance = 1200;
  let _mockAuthed  = DEV_MOCK
    && new URLSearchParams(location.search).get('signedIn') === '1';

  // The backend trusts (and clamps) the client-supplied score — the mock
  // mirrors that: pointsEarned echoes the clientScore the game computed.
  const _mock = {
    getBalance() {
      if (!_mockAuthed) return Promise.reject(_err('/game/balance', 401));
      return Promise.resolve({ balance: _mockBalance, currency: 'pts' });
    },
    startSession(_mode) {
      if (!_mockAuthed) return Promise.reject(_err('/game/session/start', 401));
      return Promise.resolve({
        sessionId: 'dev-' + Math.random().toString(36).slice(2),
        balance:   _mockBalance,
        currency:  'pts',
      });
    },
    completeSession(_sessionId, _events, clientScore) {
      if (!_mockAuthed) return Promise.reject(_err('/game/session/complete', 401));
      const pts = Math.max(0, clientScore | 0);
      _mockBalance += pts;
      return Promise.resolve({ pointsEarned: pts, balance: _mockBalance, currency: 'pts' });
    },
    submitSession(_game, _mode, _events, clientScore) {
      if (!_mockAuthed) return Promise.reject(_err('/game/session/submit', 401));
      const pts = Math.max(0, clientScore | 0);
      _mockBalance += pts;
      return Promise.resolve({
        sessionId:    'dev-' + Math.random().toString(36).slice(2),
        pointsEarned: pts,
        balance:      _mockBalance,
        currency:     'pts',
      });
    },
    abandonSession() {
      return Promise.resolve({ ok: true });
    },
  };

  // --- auth retry plumbing ---
  // On 401 from an authed call: send `auth:login_required`, wait for `auth:ready`
  // (success) or `auth:cancelled` (user dismissed login), then retry once.
  // Multiple parallel calls share a single login round-trip.

  let _authQueue     = [];
  let _authRequested = false;

  function _awaitAuth() {
    return new Promise((resolve, reject) => {
      _authQueue.push({ resolve, reject });
      if (_authRequested) return;
      _authRequested = true;
      sendToHost('auth:login_required');
      // Dev fallback: simulate a successful native login.
      if (DEV_MOCK) {
        setTimeout(() => { _mockAuthed = true; notifyAuthReady(); }, 600);
      }
    });
  }

  function notifyAuthReady() {
    const q = _authQueue; _authQueue = []; _authRequested = false;
    q.forEach(p => p.resolve());
  }

  function notifyAuthCancelled() {
    const q = _authQueue; _authQueue = []; _authRequested = false;
    q.forEach(p => p.reject(_err('auth:cancelled', 401)));
  }

  async function _withAuthRetry(fn) {
    try {
      return await fn();
    } catch (e) {
      if (e.status !== 401) throw e;
      await _awaitAuth();
      return fn();
    }
  }

  // --- real API ---

  function _err(path, status) {
    const e = new Error(`API ${path} → ${status}`);
    e.status = status;
    return e;
  }

  async function _post(path, body) {
    const res = await fetch(API_BASE + path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw _err(path, res.status);
    return res.json();
  }

  async function _get(path) {
    const res = await fetch(API_BASE + path, { credentials: 'include' });
    if (!res.ok) throw _err(path, res.status);
    return res.json();
  }

  // GET /game/balance → 200 { balance, currency } | 401 (guest, no cookie)
  // No auth retry — a 401 here is how we detect guest mode on boot.
  function getBalance() {
    return DEV_MOCK ? _mock.getBalance() : _get('/game/balance');
  }

  // POST /game/session/start → { sessionId, balance, currency }
  // Auth required. Called only for logged-in users at game start.
  // Triggers login on 401 so a guest who hits a mid-game auth gate
  // (future: stage unlock, premium stones) flows through native login.
  function startSession(mode) {
    return _withAuthRetry(() =>
      DEV_MOCK ? _mock.startSession(mode)
               : _post('/game/session/start', { game: 'rami', mode })
    );
  }

  // POST /game/session/complete → { pointsEarned, currency, walletStatus, capped }
  // Auth required. Used by the logged-in lifecycle (start → complete).
  // `clientScore` is the score the game computed; the backend clamps it to
  // the mode's maxPoints ceiling. `events` are the session audit trail.
  function completeSession(sessionId, events, clientScore) {
    return _withAuthRetry(() =>
      DEV_MOCK ? _mock.completeSession(sessionId, events, clientScore)
               : _post('/game/session/complete', { sessionId, clientScore, events })
    );
  }

  // POST /game/session/submit → { sessionId, pointsEarned, currency, walletStatus, capped }
  // Auth required. The single-call claim path: a guest plays entirely client-side,
  // then taps claim. We trigger login, then send game + mode + clientScore + the
  // full event log in one shot. The backend atomically creates a completed
  // session bound to the authenticated user and credits the wallet.
  function submitSession(mode, events, clientScore) {
    return _withAuthRetry(() =>
      DEV_MOCK ? _mock.submitSession('rami', mode, events, clientScore)
               : _post('/game/session/submit', { game: 'rami', mode, clientScore, events })
    );
  }

  // POST /game/session/abandon → { ok: true }
  // Auth required. Only called when there is a backend sessionId to abandon
  // (logged-in lifecycle). Guests have no backend session and no-op the call.
  function abandonSession(sessionId) {
    return DEV_MOCK ? _mock.abandonSession() : _post('/game/session/abandon', { sessionId });
  }

  // Public hook for callers that know up-front they are unauthenticated and
  // want to trigger the native login flow proactively instead of paying a
  // guaranteed-401 round-trip first. Fires `auth:login_required` to native
  // and resolves when `auth:ready` arrives (rejects on `auth:cancelled`).
  function requestLogin() {
    return _awaitAuth();
  }

  return {
    getBalance, startSession, completeSession, submitSession, abandonSession,
    requestLogin, notifyAuthReady, notifyAuthCancelled,
  };
})();

'use strict';

const wallet = {
  balance:  0,
  currency: 'pts',
};

const _modeBtns   = () => document.querySelectorAll('.mode-btn');
const _initError  = () => document.getElementById('initError');
const _walletPill = () => document.getElementById('wallet-pill');

// ─── Day-picker loading state ──────────────────────────────
// The day cards start life disabled (waiting on the balance probe) and are
// disabled again briefly while a session starts. Rather than leaving them
// looking frozen, a spinner replaces the › chevron. Passing a specific card
// spins only that one — and keeps it bright while the other dims — so it's
// always clear which day was chosen. With no argument both cards spin (the
// initial balance probe, where no day has been picked yet).
function setDayPickerLoading(pickedBtn) {
  _modeBtns().forEach(b => {
    b.disabled = true;
    b.classList.toggle('loading', !pickedBtn || b === pickedBtn);
  });
}
function clearDayPickerLoading() {
  _modeBtns().forEach(b => {
    b.disabled = false;
    b.classList.remove('loading');
  });
}

// Snapshot resume runs exactly once per page lifetime. auth:ready also calls
// initWallet() to refresh the wallet pill — we must not re-trigger restore on
// that second call (the in-flight _awaitAuth promise already resumes the
// pending claim in-memory).
let _bootResumeChecked = false;

async function initWallet() {
  _initError()?.classList.add('hidden');
  setDayPickerLoading();          // both cards show the spinner until balance resolves

  let authed = false;
  try {
    const data = await api.getBalance();
    wallet.balance  = data.balance;
    wallet.currency = data.currency;
    state.isGuest   = false;
    _walletPill()?.classList.remove('hidden');
    renderWallet();
    clearDayPickerLoading();
    authed = true;
  } catch (e) {
    if (e.status === 401 || e.status === 403) {
      // Guest play: no cookie. Hide the wallet pill, let the user play.
      // Login is deferred until claim/spend time.
      state.isGuest = true;
      _walletPill()?.classList.add('hidden');
      clearDayPickerLoading();
    } else {
      // Network / unexpected error — let the user retry. Skip snapshot
      // resume this turn; we'll try again on the next initWallet() call.
      wallet.balance = 0;
      renderWallet();
      // Stop the spinners but leave the cards disabled — the error message
      // tells the user to reopen, so the picker stays intentionally inert.
      _modeBtns().forEach(b => b.classList.remove('loading'));
      const err = _initError();
      if (err) {
        err.textContent = 'Could not connect. Check your connection and reopen.';
        err.classList.remove('hidden');
      }
      return;
    }
  }

  if (!_bootResumeChecked) {
    _bootResumeChecked = true;
    maybeResumeFromSnapshot(authed);
  }
}

// Boot-time snapshot resume. Branches on whether the user is now authed:
//   authed=true  → user came back signed in (cookie present). Auto-retry the
//                  claim via claimPoints; success clears the snapshot.
//   authed=false → user is still a guest (cancelled or never finished OAuth).
//                  Repaint #endOv with the claim CTA so they can re-tap.
// Stale, corrupt, or future-schema snapshots are dropped silently.
function maybeResumeFromSnapshot(isAuthedNow) {
  const snap = snapshot.load();
  if (!snap)                       return;
  if (!snapshot.isFresh(snap))     { snapshot.clear(); return; }
  if (snap.v !== 1)                { snapshot.clear(); return; }
  if (snap.kind !== 'post-game')   return;   // mid-round reserved for future

  restoreFromSnapshot(snap);

  if (isAuthedNow) {
    claimPoints();
  } else {
    showClaimCta();
  }
}

// Recovery hook for the auth:ready handler. Covers the boot race where
// native pre-injects the cookie but the WebView's first GET /game/balance
// fires before the cookie is committed — initWallet sees 401, the snapshot
// resume falls through to showClaimCta, then auth:ready arrives and
// initWallet (called again) flips state.isGuest to false. Without this,
// the user is left looking at a stale "Sign in to claim" button on an
// already-authed session. Gated by:
//   - state.restoredFromSnapshot → only fires for this boot's resume path
//   - !state.isGuest             → cookie is now valid
//   - snapshot.load()            → claim is still pending (cleared on
//                                  success / explicit exit)
//   - !claimBtn.disabled         → no manual claim already in flight
function finishPendingSnapshotClaim() {
  if (!state.restoredFromSnapshot) return;
  if (state.isGuest)               return;
  if (!snapshot.load())            return;
  const claimBtn = document.getElementById('claimBtn');
  if (!claimBtn || claimBtn.disabled) return;
  claimPoints();
}

// SDK entry point: called by bridge.js when the shell forwards a platform event.
// Native → shell (postMessage) → bridge.js → window.gameReceive
window.gameReceive = function(msg) {
  if (!msg || typeof msg !== 'object') return;
  switch (msg.type) {
    case 'wallet:update':
      wallet.balance  = msg.balance  ?? wallet.balance;
      wallet.currency = msg.currency ?? wallet.currency;
      renderWallet();
      break;
    case 'auth:ready':
      // Cookie was just injected by native. Resolve pending API retries,
      // then re-probe balance so the wallet pill appears.
      api.notifyAuthReady();
      initWallet().then(finishPendingSnapshotClaim);
      break;
    case 'auth:cancelled':
      api.notifyAuthCancelled();
      break;
    case 'game:pause':  pauseGame();  break;
    case 'game:resume': resumeGame(); break;
    case 'game:exit':   exitToMenu(); break;
  }
};

function renderWallet() {
  const el = document.getElementById('wallet-balance');
  if (el) el.textContent = `${wallet.balance} ${wallet.currency}`;
}

document.addEventListener('DOMContentLoaded', () => {
  initWallet();
});

'use strict';

function readLS(k, def) {
  const v = localStorage.getItem(k);
  if (v === null) return def;
  return v === '1' || v === 'true';
}

const settings = {
  // Surfaced in the in-game Settings sheet
  aimGuide: readLS('rami.aimGuide', true),
  reticle:  readLS('rami.reticle',  true),
  takbir:   readLS('rami.takbir',   true),
  haptic:   readLS('rami.haptic',   true),

  // Internal — not surfaced in the redesigned UI, but the engine still reads them.
  sound:        readLS('rami.sound', true),
  reduceMotion: readLS('rami.reduceMotion', matchMedia('(prefers-reduced-motion: reduce)').matches),
  wind:         false,
  hand:         localStorage.getItem('rami.hand') || 'right',
};

// Backwards-compat alias — physics.js + audio.js read settings.haptics
Object.defineProperty(settings, 'haptics', {
  enumerable: true,
  get() { return this.haptic; },
  set(v) { this.haptic = v; },
});

function saveSettings() {
  localStorage.setItem('rami.aimGuide', settings.aimGuide ? '1' : '0');
  localStorage.setItem('rami.reticle',  settings.reticle  ? '1' : '0');
  localStorage.setItem('rami.takbir',   settings.takbir   ? '1' : '0');
  localStorage.setItem('rami.haptic',   settings.haptic   ? '1' : '0');
  localStorage.setItem('rami.sound',    settings.sound    ? '1' : '0');
  localStorage.setItem('rami.reduceMotion', settings.reduceMotion ? '1' : '0');
  localStorage.setItem('rami.hand',     settings.hand);
}

// Triggers haptic feedback through two channels:
//   1. navigator.vibrate(pattern)  — Android Chrome (iOS WKWebView ignores).
//   2. sendToHost('haptic:trigger', { kind })  — bridge to native, where the
//      iOS/Android host can map `kind` to UIImpactFeedbackGenerator /
//      VibrationEffect. This is how iOS ever feels anything; until native
//      wires it up, web-vibration on Android is the only channel.
//
// `kind` is optional but recommended ('hit' | 'miss' | 'throw' | 'success').
function haptic(pattern, kind) {
  if (!settings.haptic) return;
  try { navigator.vibrate && navigator.vibrate(pattern); } catch (e) {}
  if (kind) {
    try { sendToHost('haptic:trigger', { kind }); } catch (e) {}
  }
}

'use strict';

// Resilient post-game claim snapshot.
//
// Persists state.events + display stats to localStorage immediately before the
// guest claim flow hands off to native OAuth, so a process kill, OS memory
// eviction, or WebView crash between auth:login_required and the successful
// /game/session/submit response leaves the user landing on the same end-game
// screen on next boot — not at mode select with an empty event log.
//
// The bridge contract (auth:login_required / auth:ready / auth:cancelled) is
// untouched; this is purely an additive web-only safety net. Native already
// preserves the WebView during OAuth per docs/mobile-integration-spec.md, so
// the snapshot only fires the restore path when JS context was truly lost.
//
// Trust model: `events` is already client-controlled today (any DevTools user
// can mutate state.events before tapping claim). Persisting it changes nothing
// — the backend is the authority: Integration-Server validates shape bounds
// (docs/backend-spec.md "Event-log validation"), Gamification-Server replays
// events, validates physics, caps score, and rate-limits per-user. `stats` in
// the snapshot is paint-only and is never sent to the backend.

window.snapshot = (function () {
  const KEY    = 'rami.snapshot.v1';
  const TTL_MS = 30 * 60 * 1000;          // 30 minutes — see plan rationale
  const LEGACY_KEYS = [];                 // future-proof migration list

  // Clear known legacy keys once per page load. Cheap and bounded.
  try {
    for (const k of LEGACY_KEYS) localStorage.removeItem(k);
  } catch (_) { /* private mode etc. — ignore */ }

  function save(payload) {
    try {
      localStorage.setItem(KEY, JSON.stringify(payload));
    } catch (e) {
      // QuotaExceededError, SecurityError (private mode), etc.
      // Snapshot failure must NEVER block the claim.
      console.warn('[snapshot] save failed:', e && e.name);
    }
  }

  function load() {
    let raw;
    try { raw = localStorage.getItem(KEY); }
    catch (_) { return null; }
    if (raw == null) return null;
    try { return JSON.parse(raw); }
    catch (_) { clear(); return null; }
  }

  function clear() {
    try { localStorage.removeItem(KEY); } catch (_) {}
  }

  function isFresh(snap) {
    if (!snap || typeof snap.savedAt !== 'number' || typeof snap.ttlMs !== 'number') return false;
    return snap.savedAt + snap.ttlMs > Date.now();
  }

  // Pure projection of `state` → schema payload for the post-game case.
  // Mid-round (state.running === true) is reserved for future work — the
  // `round` field stays null today and is ignored by restore.
  function buildPostGame(s) {
    const throws = s.totalThrows | 0;
    const hits   = s.totalHits   | 0;
    const accuracy = throws > 0 ? Math.round((hits / throws) * 100) : 0;
    return {
      v:         1,
      kind:      'post-game',
      savedAt:   Date.now(),
      ttlMs:     TTL_MS,
      mode:      s.modeKey,
      isGuest:   !!s.isGuest,
      sessionId: s.sessionId || null,
      events:    Array.isArray(s.events) ? s.events.slice() : [],
      stats: {
        totalHits:   hits,
        totalThrows: throws,
        bestStreak:  s.bestStreak | 0,
        accuracy:    accuracy,
        totalPoints: s.totalPoints | 0,
      },
      round: null,
    };
  }

  return { KEY, TTL_MS, save, load, clear, isFresh, buildPostGame };
})();

'use strict';

const Sfx = (() => {
  let actx, master;

  function ensure() {
    if (actx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    actx = new AC();
    master = actx.createGain();
    master.gain.value = 0.35;
    master.connect(actx.destination);
  }

  function tone(freq, dur, type = 'sine', gain = 0.3, when = 0, slide) {
    if (!settings.sound) return;
    ensure(); if (!actx) return;
    const t = actx.currentTime + when;
    const o = actx.createOscillator(), g = actx.createGain();
    o.type = type; o.frequency.setValueAtTime(freq, t);
    if (slide) o.frequency.exponentialRampToValueAtTime(slide, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(master);
    o.start(t); o.stop(t + dur + 0.05);
  }

  function noise(dur, filtFreq, q, gain, when = 0) {
    if (!settings.sound) return;
    ensure(); if (!actx) return;
    const t = actx.currentTime + when;
    const len = Math.max(1, Math.floor(actx.sampleRate * dur));
    const buf = actx.createBuffer(1, len, actx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = actx.createBufferSource(); src.buffer = buf;
    const f = actx.createBiquadFilter();
    f.type = 'bandpass'; f.frequency.value = filtFreq; f.Q.value = q;
    const g = actx.createGain(); g.gain.value = gain;
    src.connect(f); f.connect(g); g.connect(master);
    src.start(t); src.stop(t + dur + 0.05);
  }

  return {
    resume() { ensure(); if (actx && actx.state === 'suspended') actx.resume(); },
    throw_() { noise(0.14, 2800, 1.6, 0.18); tone(700, 0.08, 'sine', 0.05, 0, 300); },
    hit()    { noise(0.22, 380, 1.2, 0.40); tone(130, 0.16, 'triangle', 0.10, 0, 70); },
    miss()   { noise(0.28, 180, 0.9, 0.22); },
    stage()  { tone(660, 0.18, 'sine', 0.22); tone(880, 0.24, 'sine', 0.22, 0.17); },
    done()   { tone(523, 0.22, 'sine', 0.22); tone(659, 0.22, 'sine', 0.22, 0.2); tone(784, 0.4, 'sine', 0.22, 0.42); },
  };
})();

'use strict';

const state = {
  modeKey: null,
  stages: [],
  stageIdx: 0,
  stonesLeft: 0,
  totalHits: 0, totalThrows: 0,
  streak: 0, bestStreak: 0,
  running: false, paused: false,
  stone: null,
  swipe: null,
  particles: [], splash: [],
  motes: [],
  scorePops: [],         // floating "+N" pts text spawned per hit
  totalPoints: 0,        // local accumulator — sum of per-hit pts
  // The visible pile of pebbles the player can pick from. Built once per
  // stage by buildHeap() in main.js. Each entry is parametric; the screen
  // anchor for index i is resolved at draw/pick time by heapAnchor(i) in
  // renderer.js, so a resize doesn't need to mutate this list.
  heap: [],              // [{ isReady, lump, thrown }]
  floatMsg: '', floatMsgT: 0,
  shake: 0, cooldown: 0, slowMo: 1.0, slowMoT: 0,
  wind: 0, windT: 0,
  takbirCount: 0,
  firstThrowDone: false,

  // Gamification session (Pattern D)
  sessionId: null,
  sessionStartT: 0,
  events: [],           // GameEvent[] — sent to backend on session complete

  // Auth state — true until GET /game/balance returns 200
  isGuest: true,

  // True if this page boot was hydrated from a localStorage snapshot
  // (see snapshot.js + maybeResumeFromSnapshot in wallet.js). Telemetry only.
  restoredFromSnapshot: false,
};

(function seedMotes() {
  const N = 18;
  for (let i = 0; i < N; i++) {
    state.motes.push({
      x: (Math.random() - 0.5) * 14,
      y: 0.3 + Math.random() * 3.2,
      z: 2 + Math.random() * 14,
      vx: (Math.random() - 0.5) * 0.25,
      vy: -0.05 + Math.random() * 0.12,
      vz: (Math.random() - 0.5) * 0.18,
      phase: Math.random() * Math.PI * 2,
      size: 0.6 + Math.random() * 0.7,
    });
  }
})();

'use strict';

const canvas = document.getElementById('game');
// `ctx` is `let` so that renderStaticBg() can briefly retarget it at the
// offscreen background canvas (the existing draw helpers all reach for
// this global). It is otherwise the main canvas's 2D context.
let ctx = canvas.getContext('2d');
let W = 0, H = 0, DPR = 1;

// Offscreen background — sky / landscape / ground / bridge crowd are
// effectively static; painting them every frame is the dominant lag
// source on lower-end devices (40 createLinearGradient per crowd is
// expensive). We paint them once onto bgCanvas in resize() and blit
// each frame in draw(). Dynamic layers (walls, motes, particles, etc.)
// are still drawn live on top.
let bgCanvas = null;
let bgCtx    = null;

function resize() {
  DPR = Math.min(window.devicePixelRatio || 1, 2);
  W = window.innerWidth; H = window.innerHeight;
  canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
  canvas.width = W * DPR; canvas.height = H * DPR;
  ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  renderStaticBg();
}

function renderStaticBg() {
  if (W === 0 || H === 0) return;
  if (!bgCanvas) {
    bgCanvas = document.createElement('canvas');
    bgCtx    = bgCanvas.getContext('2d');
  }
  bgCanvas.width  = Math.round(W * DPR);
  bgCanvas.height = Math.round(H * DPR);
  bgCtx.setTransform(DPR, 0, 0, DPR, 0, 0);

  // Retarget the global ctx while painting the cache so the existing
  // draw helpers (drawSky / drawDistantScape / drawGround / drawBridgeCrowd)
  // work without per-function refactoring.
  const main = ctx;
  ctx = bgCtx;
  try {
    const horY = horizonY();
    drawSky(horY);
    drawDistantScape(horY);
    drawGround(horY);
    drawBridgeCrowd();
  } finally {
    ctx = main;
  }
}

function horizonY() { return H * 0.55; }

function project(x, y, z) {
  if (z <= 0.1) return { sx: W / 2, sy: horizonY(), scale: 0, visible: false };
  const scale = FOCAL / z;
  return { sx: W / 2 + x * scale, sy: horizonY() - (y - EYE_H) * scale, scale, visible: true };
}

function shadeRGB(r, g, b, f) {
  return `rgb(${Math.max(0, Math.min(255, Math.round(r * f)))},` +
             `${Math.max(0, Math.min(255, Math.round(g * f)))},` +
             `${Math.max(0, Math.min(255, Math.round(b * f)))})`;
}

function roundRect(x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ---- Sky, landscape, ground ----

function drawSky(horY) {
  const sky = ctx.createLinearGradient(0, 0, 0, horY);
  sky.addColorStop(0,    '#a6cae8');
  sky.addColorStop(0.55, '#cdd9d8');
  sky.addColorStop(0.85, '#e6d8b6');
  sky.addColorStop(1,    '#d4b88a');
  ctx.fillStyle = sky; ctx.fillRect(0, 0, W, horY);

  // Sun glow
  const sunX = W * 0.72, sunY = horY * 0.3;
  const sunR = Math.min(360, W * 0.55);
  const bloom = ctx.createRadialGradient(sunX, sunY, 4, sunX, sunY, sunR);
  bloom.addColorStop(0,    'rgba(255, 250, 230, 0.65)');
  bloom.addColorStop(0.2,  'rgba(255, 244, 212, 0.22)');
  bloom.addColorStop(1,    'rgba(255, 240, 200, 0)');
  ctx.fillStyle = bloom; ctx.fillRect(0, 0, W, horY);

  // Sparse cloud streaks
  ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
  for (let i = 0; i < 4; i++) {
    const cx = (i * 230 + 60) % W;
    const cy = horY * (0.18 + (i % 2) * 0.07);
    ctx.beginPath();
    ctx.ellipse(cx, cy, 60 + (i % 3) * 12, 6, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  const haze = ctx.createLinearGradient(0, horY - 90, 0, horY);
  haze.addColorStop(0, 'rgba(240, 225, 195, 0)');
  haze.addColorStop(1, 'rgba(240, 225, 195, 0.50)');
  ctx.fillStyle = haze; ctx.fillRect(0, horY - 90, W, 90);
}

function drawDistantScape(horY) {
  // Far mountain ridges (Mecca hills)
  ctx.fillStyle = 'rgba(140, 122, 108, 0.55)';
  ctx.beginPath(); ctx.moveTo(0, horY);
  for (let x = 0; x <= W; x += 28) {
    const y = horY - 38 - Math.sin(x * 0.004) * 16 - Math.sin(x * 0.013 + 2.1) * 9;
    ctx.lineTo(x, y);
  }
  ctx.lineTo(W, horY); ctx.closePath(); ctx.fill();

  ctx.fillStyle = 'rgba(110, 92, 80, 0.55)';
  ctx.beginPath(); ctx.moveTo(0, horY);
  for (let x = 0; x <= W; x += 24) {
    const y = horY - 18 - Math.sin(x * 0.008 + 1.1) * 10 - Math.sin(x * 0.02) * 4;
    ctx.lineTo(x, y);
  }
  ctx.lineTo(W, horY); ctx.closePath(); ctx.fill();

  const wash = ctx.createLinearGradient(0, horY - 16, 0, horY + 4);
  wash.addColorStop(0, 'rgba(240, 225, 195, 0)');
  wash.addColorStop(1, 'rgba(240, 225, 195, 0.40)');
  ctx.fillStyle = wash; ctx.fillRect(0, horY - 16, W, 20);
}

let groundNoiseCanvas = null, groundNoiseW = 0, groundNoiseH = 0;
function getGroundNoise() {
  if (groundNoiseCanvas && groundNoiseW === W && groundNoiseH === H) return groundNoiseCanvas;
  const c = document.createElement('canvas');
  c.width = W; c.height = Math.max(1, Math.floor(H * 0.55));
  const g = c.getContext('2d');
  const id = g.createImageData(c.width, c.height);
  const data = id.data;
  for (let i = 0; i < data.length; i += 4) {
    const v = (Math.random() - 0.5) * 22;
    data[i]   = 145 + v * 0.6;
    data[i+1] = 122 + v * 0.4;
    data[i+2] =  92 + v * 0.2;
    data[i+3] = Math.random() < 0.30 ? 16 : 0;
  }
  g.putImageData(id, 0, 0);
  groundNoiseCanvas = c; groundNoiseW = W; groundNoiseH = H;
  return c;
}

function drawGround(horY) {
  // Paved courtyard — warm tan/grey concrete
  const gnd = ctx.createLinearGradient(0, horY, 0, H);
  gnd.addColorStop(0,   '#b8a282');
  gnd.addColorStop(0.4, '#9a8265');
  gnd.addColorStop(1,   '#6e5840');
  ctx.fillStyle = gnd; ctx.fillRect(0, horY, W, H - horY);

  if (!settings.reduceMotion) {
    ctx.globalAlpha = 0.40;
    ctx.drawImage(getGroundNoise(), 0, horY);
    ctx.globalAlpha = 1;
  }

  const warm = ctx.createLinearGradient(0, horY, 0, horY + 80);
  warm.addColorStop(0, 'rgba(255, 232, 190, 0.16)');
  warm.addColorStop(1, 'rgba(255, 232, 190, 0)');
  ctx.fillStyle = warm; ctx.fillRect(0, horY, W, 80);

  const vg = ctx.createRadialGradient(
    W / 2, H * 0.78, Math.min(W, H) * 0.28,
    W / 2, H * 0.78, Math.max(W, H) * 0.7
  );
  vg.addColorStop(0, 'rgba(30, 15, 0, 0)');
  vg.addColorStop(1, 'rgba(30, 15, 0, 0.55)');
  ctx.fillStyle = vg; ctx.fillRect(0, horY, W, H - horY);
}

// ---- Upper-bridge crowd band (multi-level Jamarat structure) ----
//
// A horizontal band of pilgrims walking on the upper bridge level behind the
// pillar, plus a railing line and lamp-post silhouettes. Drawn before the
// pillar so the wall occludes the middle portion.

function drawBridgeCrowd() {
  const z = 24;
  const railY = 1.95;     // bridge railing height (world units)
  const railTop = project(0, railY, z);
  const railBot = project(0, railY - 0.18, z);
  if (!railTop.visible) return;
  const yT = railTop.sy;
  const yB = railBot.sy;

  // Bridge deck plate (under the railing)
  const deckGrad = ctx.createLinearGradient(0, yB, 0, yB + 24);
  deckGrad.addColorStop(0, 'rgba(150, 132, 108, 0.85)');
  deckGrad.addColorStop(1, 'rgba(120, 100, 80, 0)');
  ctx.fillStyle = deckGrad;
  ctx.fillRect(0, yB, W, 24);

  // Railing line (top)
  ctx.strokeStyle = 'rgba(60, 45, 30, 0.55)';
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(0, yT); ctx.lineTo(W, yT); ctx.stroke();

  // Pilgrims — 40 packed across the band, all in white iḥrām. Even at this
  // distance they read as people, not silhouettes: shaded robes + skin heads.
  for (let i = 0; i < 40; i++) {
    const x = (i + 0.5) * (W / 40) + ((i * 13) % 5);
    const headR = 4 + ((i * 7) % 3);
    const bodyH = 16 + ((i * 11) % 5);
    // Body — white robe with soft side shading for volume
    const bg = ctx.createLinearGradient(x - bodyH * 0.32, 0, x + bodyH * 0.32, 0);
    bg.addColorStop(0,   '#d6c8a4');
    bg.addColorStop(0.5, '#f5edd4');
    bg.addColorStop(1,   '#d6c8a4');
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.moveTo(x - bodyH * 0.32, yT);
    ctx.quadraticCurveTo(x - bodyH * 0.27, yT - bodyH * 0.86,
                         x - bodyH * 0.17, yT - bodyH);
    ctx.lineTo(x + bodyH * 0.17, yT - bodyH);
    ctx.quadraticCurveTo(x + bodyH * 0.27, yT - bodyH * 0.86,
                         x + bodyH * 0.32, yT);
    ctx.closePath();
    ctx.fill();
    // Head — skin tone with a touch of shadow
    const hy = yT - bodyH - headR * 0.5;
    ctx.fillStyle = '#bd9162';
    ctx.beginPath();
    ctx.arc(x, hy, headR, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(60,40,20,0.22)';
    ctx.beginPath();
    ctx.arc(x + headR * 0.3, hy + headR * 0.18, headR * 0.7, 0, Math.PI * 2);
    ctx.fill();
  }

  // Lamp posts — 4 evenly spaced
  for (let i = 0; i < 4; i++) {
    const lx = (i + 0.5) * (W / 4);
    const baseY = yT;
    const topY  = yT - 64;
    ctx.strokeStyle = 'rgba(40, 28, 18, 0.65)';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(lx, baseY); ctx.lineTo(lx, topY); ctx.stroke();
    // Lamp head
    ctx.fillStyle = 'rgba(255, 234, 178, 0.95)';
    ctx.beginPath();
    ctx.ellipse(lx, topY - 4, 6, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    // Bloom
    const lb = ctx.createRadialGradient(lx, topY - 4, 1, lx, topY - 4, 20);
    lb.addColorStop(0, 'rgba(255, 234, 178, 0.55)');
    lb.addColorStop(1, 'rgba(255, 234, 178, 0)');
    ctx.fillStyle = lb;
    ctx.beginPath(); ctx.arc(lx, topY - 4, 20, 0, Math.PI * 2); ctx.fill();
  }
}

// ---- Crowd of pilgrims around the pillar ----
//
// Every figure sits BEHIND or BESIDE the pillar — angles stay in [0, π] so
// sin >= 0 keeps them out of the throwing lane between the player and the
// wall. The player must never appear to be stoning people.

const PILGRIM_RING = (function buildPilgrimRing() {
  const ring = [];
  // A Ḥajj crowd at the Jamarāt is a sea of white iḥrām — every figure white.
  // Inner arc — flanks the pillar; centre figures occluded by it
  for (let i = 0; i < 20; i++) {
    const ang = Math.PI * (0.02 + 0.96 * (i / 19));
    ring.push([ang, 0.4, 0.56 + (i % 3) * 0.04, 'ihram']);
  }
  // Middle arc
  for (let i = 0; i < 28; i++) {
    const ang = Math.PI * (i / 27);
    ring.push([ang, 1.9, 0.46 + (i % 3) * 0.03, 'ihram']);
  }
  // Outer arc — furthest, smallest, densest (blends toward the bridge)
  for (let i = 0; i < 36; i++) {
    const ang = Math.PI * (i / 35);
    ring.push([ang, 3.8, 0.36 + (i % 2) * 0.03, 'ihram']);
  }
  return ring;
})();

// ---- Stacked-block stone pillar inside a sandstone basin ----

function drawWall(stage, isActive) {
  const def = stage.def;
  const wx = 0, wz = 14;
  const a = def.w / 2, b = def.d / 2, h = def.h;
  const wob = Math.sin(Date.now() * 0.025) * stage.wobble * 2.5;

  // ── Contact shadow on the pavement ──
  {
    const NS = 28;
    const pts = [];
    const shA = a * 1.6, shB = b * 2.6;
    for (let i = 0; i < NS; i++) {
      const t = (i / NS) * Math.PI * 2;
      pts.push(project(wx + shA * Math.cos(t) + wob, 0, wz + shB * Math.sin(t)));
    }
    const center = project(wx + wob, 0, wz);
    ctx.beginPath();
    ctx.moveTo(pts[0].sx, pts[0].sy);
    for (let i = 1; i < NS; i++) ctx.lineTo(pts[i].sx, pts[i].sy);
    ctx.closePath();
    const sg = ctx.createRadialGradient(
      center.sx, center.sy + 2, 4,
      center.sx, center.sy + 2, Math.max(40, (pts[0].sx - pts[Math.floor(NS / 2)].sx))
    );
    sg.addColorStop(0,   'rgba(30, 18, 6, 0.42)');
    sg.addColorStop(0.6, 'rgba(30, 18, 6, 0.14)');
    sg.addColorStop(1,   'rgba(30, 18, 6, 0)');
    ctx.fillStyle = sg; ctx.fill();
  }

  // ── Crowd — gathered behind + beside the pillar only. Drawn first so the
  //    basin and pillar occlude the figures standing behind them. ──
  if (isActive) {
    for (const [ang, dz, sc, kind] of PILGRIM_RING) {
      drawPilgrimAt(wx + wob, wz, a, b, ang, dz, sc, kind);
    }
  }

  // (No separate basin floor — the rounded pillar base plugs the basin hole.)

  // Projected point on the egg-profiled pillar surface at angle t, height y.
  const pp = (t, y) => {
    const f = pillarRadiusFactor(y / h);
    return project(wx + a * f * Math.cos(t) + wob, y, wz + b * f * Math.sin(t));
  };

  // ── Pillar body — egg-profiled sandstone, drawn as curved-side strips ──
  const N  = 22;
  const LV = 16;                      // vertical samples → smooth bottom round
  const sunXZ = { x: -0.55, z: -0.83 };
  const fMid  = pillarRadiusFactor(0.5);
  const segments = [];
  for (let i = 0; i < N; i++) {
    const t1 = Math.PI + (i / N) * Math.PI;
    const t2 = Math.PI + ((i + 1) / N) * Math.PI;
    const left = [], right = [];
    for (let k = 0; k <= LV; k++) {
      const y = (k / LV) * h;
      left.push(pp(t1, y));
      right.push(pp(t2, y));
    }
    const tm = (t1 + t2) / 2;
    let nx = Math.cos(tm) / a, nz = Math.sin(tm) / b;
    const nLen = Math.hypot(nx, nz) || 1;
    nx /= nLen; nz /= nLen;
    const dot = nx * sunXZ.x + nz * sunXZ.z;
    const shade = Math.max(0.55, Math.min(1.05, 0.82 + dot * 0.22));
    segments.push({ left, right, shade, zMid: wz + b * fMid * Math.sin(tm) });
  }
  segments.sort((p, q) => q.zMid - p.zMid);

  for (const s of segments) {
    const grad = ctx.createLinearGradient(0, s.left[LV].sy,
                                          0, Math.max(s.left[0].sy, s.right[0].sy));
    // Sandstone — gradient is kept warm all the way down so the pillar's base
    // blends into the rim (#cdae78 ≈ 205,174,120) with no harsh dark step.
    grad.addColorStop(0,    shadeRGB(228, 210, 162, s.shade));
    grad.addColorStop(0.6,  shadeRGB(212, 184, 132, s.shade));
    grad.addColorStop(1,    shadeRGB(204, 174, 122, s.shade));
    ctx.fillStyle = grad;
    ctx.strokeStyle = grad;       // a hairline stroke seals seams between strips
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(s.left[0].sx, s.left[0].sy);
    for (let k = 1; k <= LV; k++) ctx.lineTo(s.left[k].sx, s.left[k].sy);
    for (let k = LV; k >= 0; k--) ctx.lineTo(s.right[k].sx, s.right[k].sy);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  // ── Coursed-masonry texture (horizontal courses + offset vertical seams) ──
  ctx.save();
  ctx.beginPath();
  const NW = 48;
  // bottom rim arc (y = 0), left → right
  for (let i = 0; i <= NW; i++) {
    const p = pp(Math.PI + (i / NW) * Math.PI, 0);
    if (i === 0) ctx.moveTo(p.sx, p.sy); else ctx.lineTo(p.sx, p.sy);
  }
  // right edge up — follows the egg bulge
  for (let k = 1; k <= LV; k++) {
    const p = pp(2 * Math.PI, (k / LV) * h);
    ctx.lineTo(p.sx, p.sy);
  }
  // top rim arc (y = h), right → left
  for (let i = NW; i >= 0; i--) {
    const p = pp(Math.PI + (i / NW) * Math.PI, h);
    ctx.lineTo(p.sx, p.sy);
  }
  // left edge down
  for (let k = LV - 1; k >= 1; k--) {
    const p = pp(Math.PI, (k / LV) * h);
    ctx.lineTo(p.sx, p.sy);
  }
  ctx.closePath();
  ctx.clip();

  const COURSES = 9;
  const COLS    = 14;
  // Horizontal courses
  for (let r = 1; r < COURSES; r++) {
    const yLine = (r / COURSES) * h;
    ctx.strokeStyle = 'rgba(70, 50, 24, 0.30)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= 40; i++) {
      const p = pp(Math.PI + (i / 40) * Math.PI, yLine);
      if (i === 0) ctx.moveTo(p.sx, p.sy); else ctx.lineTo(p.sx, p.sy);
    }
    ctx.stroke();
  }
  // Vertical seams — offset every other course (running bond)
  for (let r = 0; r < COURSES; r++) {
    const yA = (r       / COURSES) * h;
    const yB = ((r + 1) / COURSES) * h;
    const offset = (r % 2) * 0.5;
    for (let c = 0; c <= COLS; c++) {
      const t = Math.PI + ((c + offset) / COLS) * Math.PI;
      if (t >= 2 * Math.PI) continue;
      const bot = pp(t, yA);
      const top = pp(t, yB);
      ctx.strokeStyle = 'rgba(70, 50, 24, 0.28)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(bot.sx, bot.sy);
      ctx.lineTo(top.sx, top.sy);
      ctx.stroke();
    }
  }
  // Small lighter highlights on individual blocks (deterministic)
  ctx.globalAlpha = 0.18;
  for (let i = 0; i < 80; i++) {
    const r1 = ((Math.sin(i * 12.9898 + 3.7) * 43758.5453) % 1 + 1) % 1;
    const r2 = ((Math.sin(i * 78.233  + 9.1) * 43758.5453) % 1 + 1) % 1;
    const p   = pp(Math.PI + r1 * Math.PI, r2 * h);
    ctx.fillStyle = '#f3e8c8';
    ctx.fillRect(p.sx - 4, p.sy - 2, 8, 4);
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  // ── No top cap. From the thrower's low POV the pillar simply runs up out
  //    of frame — the masonry body's own top edge is all that's drawn. ──

  // ── Subtle dark scuff marks on hit positions ──
  for (const p of stage.landedPebbles) {
    const onFace = p.y != null && p.y > 0.2 && p.z < wz;
    if (!onFace) continue;
    const pp = project(p.x + wob * 0.3, p.y, p.z);
    if (!pp.visible) continue;
    const r = Math.max(1.4, (p.size || 0.06) * pp.scale * 0.8);
    const g = ctx.createRadialGradient(pp.sx, pp.sy, 0.5, pp.sx, pp.sy, r * 1.5);
    g.addColorStop(0,   'rgba(245, 232, 200, 0.55)');
    g.addColorStop(0.7, 'rgba(245, 232, 200, 0.15)');
    g.addColorStop(1,   'rgba(245, 232, 200, 0)');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(pp.sx, pp.sy, r * 1.5, 0, Math.PI * 2); ctx.fill();
  }

  // ── Basin wall — drawn AFTER the pillar so it hides the pillar's base; the
  //    pillar then reads as rising up out of a raised sandstone basement. ──
  drawBasinWall(wx + wob, wz, a, b);
}

// Basin geometry. Inner opening (inA/inB) is set to exactly the pillar's
// radius (pillarRadiusFactor = 1.5), so the rim's inner ellipse falls on the
// same curve as the pillar's bottom edge — they read as a single continuous
// outline. The rim's inner edge is also never stroked.
const BASIN = { outA: 2.15, outB: 3.1, inA: 1.5, inB: 1.5, wallH: 1.15 };

// Sandstone basin wall + rim — drawn AFTER the pillar so the wall covers the
// pillar's base (the pillar appears to rise out of a raised basement).
function drawBasinWall(wx, wz, a, b) {
  const outA = a * BASIN.outA, outB = b * BASIN.outB;
  const inA  = a * BASIN.inA,  inB  = b * BASIN.inB;
  const wallH = BASIN.wallH;

  // Front face — the near semicircle of the basin ring
  const N = 30;
  for (let i = 0; i < N; i++) {
    const t1 = Math.PI + (i / N) * Math.PI;
    const t2 = Math.PI + ((i + 1) / N) * Math.PI;
    const bl = project(wx + outA * Math.cos(t1), 0,     wz + outB * Math.sin(t1));
    const br = project(wx + outA * Math.cos(t2), 0,     wz + outB * Math.sin(t2));
    const tl = project(wx + outA * Math.cos(t1), wallH, wz + outB * Math.sin(t1));
    const tr = project(wx + outA * Math.cos(t2), wallH, wz + outB * Math.sin(t2));
    if (!bl.visible && !br.visible) continue;
    const tm = (t1 + t2) / 2;
    const shade = Math.max(0.72, 0.94 + Math.sin(tm) * 0.14);
    const grad = ctx.createLinearGradient(0, tl.sy, 0, bl.sy);
    grad.addColorStop(0, shadeRGB(224, 208, 162, shade));
    grad.addColorStop(1, shadeRGB(168, 140, 96,  shade * 0.95));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(bl.sx, bl.sy);
    ctx.lineTo(br.sx, br.sy);
    ctx.lineTo(tr.sx, tr.sy);
    ctx.lineTo(tl.sx, tl.sy);
    ctx.closePath(); ctx.fill();
  }

  // Top rim — fill the ring (between outer + inner ellipse)...
  ctx.beginPath();
  for (let i = 0; i <= 48; i++) {
    const t = (i / 48) * Math.PI * 2;
    const p = project(wx + outA * Math.cos(t), wallH, wz + outB * Math.sin(t));
    if (i === 0) ctx.moveTo(p.sx, p.sy); else ctx.lineTo(p.sx, p.sy);
  }
  for (let i = 48; i >= 0; i--) {
    const t = (i / 48) * Math.PI * 2;
    const p = project(wx + inA * Math.cos(t), wallH, wz + inB * Math.sin(t));
    ctx.lineTo(p.sx, p.sy);
  }
  ctx.closePath();
  ctx.fillStyle = '#cdae78';
  ctx.fill();
  // ...then outline ONLY the outer edge. The inner edge is intentionally
  // left unstroked so it never reads as a separate ring around the pillar —
  // the rounded pillar base covers it.
  ctx.beginPath();
  for (let i = 0; i <= 48; i++) {
    const t = (i / 48) * Math.PI * 2;
    const p = project(wx + outA * Math.cos(t), wallH, wz + outB * Math.sin(t));
    if (i === 0) ctx.moveTo(p.sx, p.sy); else ctx.lineTo(p.sx, p.sy);
  }
  ctx.closePath();
  ctx.strokeStyle = 'rgba(70, 50, 24, 0.35)';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Masonry texture on the front face (clipped)
  ctx.save();
  ctx.beginPath();
  for (let i = 0; i <= 30; i++) {
    const t = Math.PI + (i / 30) * Math.PI;
    const p = project(wx + outA * Math.cos(t), 0, wz + outB * Math.sin(t));
    if (i === 0) ctx.moveTo(p.sx, p.sy); else ctx.lineTo(p.sx, p.sy);
  }
  for (let i = 30; i >= 0; i--) {
    const t = Math.PI + (i / 30) * Math.PI;
    const p = project(wx + outA * Math.cos(t), wallH, wz + outB * Math.sin(t));
    ctx.lineTo(p.sx, p.sy);
  }
  ctx.closePath();
  ctx.clip();
  for (const yf of [0.34, 0.67]) {
    ctx.strokeStyle = 'rgba(70, 50, 24, 0.30)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= 40; i++) {
      const t = Math.PI + (i / 40) * Math.PI;
      const p = project(wx + outA * Math.cos(t), wallH * yf, wz + outB * Math.sin(t));
      if (i === 0) ctx.moveTo(p.sx, p.sy); else ctx.lineTo(p.sx, p.sy);
    }
    ctx.stroke();
  }
  const BLOCKS = 22;
  for (let row = 0; row < 3; row++) {
    const yA = (row / 3) * wallH;
    const yB = ((row + 1) / 3) * wallH;
    const offset = (row % 2) * 0.5;
    for (let c = 0; c <= BLOCKS; c++) {
      const t = Math.PI + ((c + offset) / BLOCKS) * Math.PI;
      if (t >= 2 * Math.PI) continue;
      const bot = project(wx + outA * Math.cos(t), yA, wz + outB * Math.sin(t));
      const top = project(wx + outA * Math.cos(t), yB, wz + outB * Math.sin(t));
      if (!bot.visible || !top.visible) continue;
      ctx.strokeStyle = 'rgba(70, 50, 24, 0.26)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(bot.sx, bot.sy);
      ctx.lineTo(top.sx, top.sy);
      ctx.stroke();
    }
  }
  ctx.restore();
}

// One pilgrim placed parametrically behind/beside the wall — every figure is
// in white iḥrām. `dz` pushes the figure further back from the pillar.
function drawPilgrimAt(wx, wz, a, b, ang, dz, sc, kind) {
  const radius = a * 1.7 + dz * 0.7;
  const px = wx + Math.cos(ang) * radius;
  const pz = wz + Math.sin(ang) * b * 1.9 + dz + 0.4;
  const foot = project(px, 0, pz);
  const head = project(px, 1.66 * sc, pz);
  if (!foot.visible || !head.visible) return;
  drawPilgrim(foot.sx, foot.sy, head.sy, kind || 'ihram');
}

function drawPilgrim(footX, footY, headY, kind) {
  const h = footY - headY;
  if (h < 5) return;
  const hr = h * 0.155;
  const headCY = headY + hr;
  const shoulderY = headY + hr * 1.95;
  const shoulderHalf = h * 0.25;
  const hipHalf = h * 0.21;

  // ground shadow
  ctx.fillStyle = 'rgba(0,0,0,0.16)';
  ctx.beginPath();
  ctx.ellipse(footX, footY, shoulderHalf * 0.72, h * 0.03, 0, 0, Math.PI * 2);
  ctx.fill();

  // Every watcher wears white iḥrām — bright cloth, warm skin.
  const robeMid  = '#fbf6e8';
  const robeEdge = '#d3c4a0';
  const headLit  = '#c89567';
  const headDk   = '#8a6238';
  const outline  = 'rgba(110,86,50,0.34)';

  // arms — robe sleeves hanging at the sides, drawn behind the body
  ctx.strokeStyle = robeEdge;
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(1.4, h * 0.10);
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(footX + s * shoulderHalf * 0.74, shoulderY + h * 0.04);
    ctx.quadraticCurveTo(footX + s * shoulderHalf * 0.98, shoulderY + h * 0.34,
                         footX + s * hipHalf * 0.94, footY - h * 0.16);
    ctx.stroke();
  }
  ctx.lineCap = 'butt';

  // robe — rounded shoulders, slight taper; sideways gradient gives volume
  ctx.beginPath();
  ctx.moveTo(footX - hipHalf, footY);
  ctx.quadraticCurveTo(footX - shoulderHalf, shoulderY + h * 0.07,
                       footX - shoulderHalf * 0.72, shoulderY - h * 0.05);
  ctx.quadraticCurveTo(footX, shoulderY - h * 0.11,
                       footX + shoulderHalf * 0.72, shoulderY - h * 0.05);
  ctx.quadraticCurveTo(footX + shoulderHalf, shoulderY + h * 0.07,
                       footX + hipHalf, footY);
  ctx.closePath();
  const g = ctx.createLinearGradient(footX - shoulderHalf, 0, footX + shoulderHalf, 0);
  g.addColorStop(0,    robeEdge);
  g.addColorStop(0.42, robeMid);
  g.addColorStop(0.62, robeMid);
  g.addColorStop(1,    robeEdge);
  ctx.fillStyle = g;
  ctx.fill();
  ctx.strokeStyle = outline;
  ctx.lineWidth = Math.max(0.6, h * 0.018);
  ctx.stroke();

  // iḥrām drape — a diagonal fold across the chest + soft vertical creases
  ctx.strokeStyle = 'rgba(150,120,72,0.26)';
  ctx.lineWidth = Math.max(0.6, h * 0.02);
  ctx.beginPath();
  ctx.moveTo(footX - shoulderHalf * 0.62, shoulderY - h * 0.02);
  ctx.quadraticCurveTo(footX, shoulderY + h * 0.12,
                       footX + shoulderHalf * 0.74, shoulderY + h * 0.16);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(150,120,72,0.16)';
  ctx.lineWidth = Math.max(0.5, h * 0.014);
  for (const f of [-0.32, 0.3]) {
    ctx.beginPath();
    ctx.moveTo(footX + shoulderHalf * f * 0.7, shoulderY + h * 0.12);
    ctx.lineTo(footX + hipHalf * f, footY - h * 0.04);
    ctx.stroke();
  }

  // head — skin with a soft radial shade so it reads as a face, not a dot
  const hg = ctx.createRadialGradient(
    footX - hr * 0.3, headCY - hr * 0.32, hr * 0.2,
    footX, headCY, hr * 1.05);
  hg.addColorStop(0, headLit);
  hg.addColorStop(1, headDk);
  ctx.fillStyle = hg;
  ctx.beginPath();
  ctx.arc(footX, headCY, hr, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = outline;
  ctx.lineWidth = Math.max(0.5, h * 0.014);
  ctx.stroke();

  // faint neck shadow where the head meets the robe
  ctx.fillStyle = 'rgba(60,40,20,0.16)';
  ctx.beginPath();
  ctx.ellipse(footX, headCY + hr * 0.92, hr * 0.55, hr * 0.3, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ---- Hero — the player's pebbles, heaped in the foreground ----
//
// No figure, no hand: just the pilgrim's own pebbles resting at the bottom
// of the screen. The front pebble is the one about to be thrown and pulses
// gently while a throw is available. The heap shrinks as stones are spent.

// Resolve the screen-space anchor (cx, cy, r) for heap stone i.
// Kept outside drawHero so the input layer (pickHeapStone in input.js)
// can hit-test against the exact same coordinates without duplicating
// the layout math. Stable across resizes — depends only on W, H, i.
function heapAnchor(i) {
  const cx = W / 2;
  const S  = Math.min(W * 0.52, H * 0.42);
  // Lifted off the very bottom edge — touch swipes that start in the
  // last ~40 px get hijacked by the OS home-bar/app-switcher gesture on
  // iOS and Android, so the pile (and therefore the swipe origin) needs
  // headroom above the system gesture zone.
  const baseY = H - S * 0.26;
  const r0 = S * 0.050;
  const rnd = (k) => {
    const v = Math.sin(k * 12.9898 + 4.13) * 43758.5453;
    return v - Math.floor(v);
  };
  if (i === 0) {
    return { x: cx, y: baseY - r0 * 0.35, r: r0 * 1.18 };
  }
  // i = 1..N-1 — backing stones use (i-1) as the rnd seed offset so the
  // layout matches the original drawHero math exactly.
  const j = i - 1;
  const dx = (rnd(j * 3 + 1) - 0.5) * S * 0.62;
  const dy = -rnd(j * 3 + 2) * r0 * 1.5;
  const rr = r0 * (0.74 + rnd(j * 3 + 3) * 0.4);
  return { x: cx + dx, y: baseY + dy, r: rr };
}

function drawHero() {
  if (!state.running) return;
  const stage = state.stages[state.stageIdx];
  if (!stage) return;
  if (!state.heap || state.heap.length === 0) return;

  const cx = W / 2;
  const S  = Math.min(W * 0.52, H * 0.42);
  // Lifted off the very bottom edge — touch swipes that start in the
  // last ~40 px get hijacked by the OS home-bar/app-switcher gesture on
  // iOS and Android, so the pile (and therefore the swipe origin) needs
  // headroom above the system gesture zone.
  const baseY = H - S * 0.26;
  const r0 = S * 0.050;

  // ── soft contact shadow under the heap (drawn even when stones thrown,
  //    so the pile foot doesn't pop out as pebbles are removed) ──
  const sh = ctx.createRadialGradient(cx, baseY + r0 * 0.4, r0 * 0.3,
                                      cx, baseY + r0 * 0.4, S * 0.46);
  sh.addColorStop(0, 'rgba(20,12,4,0.42)');
  sh.addColorStop(1, 'rgba(20,12,4,0)');
  ctx.fillStyle = sh;
  ctx.beginPath();
  ctx.ellipse(cx, baseY + r0 * 0.4, S * 0.46, r0 * 0.78, 0, 0, Math.PI * 2);
  ctx.fill();

  const pickedIdx = state.swipe && state.swipe.pickedIndex != null
    ? state.swipe.pickedIndex : -1;

  // Compute the screen position to draw the picked stone at. Default is
  // the heap anchor — but if the player is actively dragging, the stone
  // tracks the finger so left/right input is *immediately* visible on
  // the pebble itself (no need to look at the arc). On release without
  // a throw, the stone briefly animates back to its anchor (returnT
  // counts down in physics.update).
  function pickedDrawPos(i, anchor) {
    if (i === pickedIdx && state.swipe) {
      // Rubber-band from the anchor — the stone stays in the pile and
      // moves WITH the drag delta, not snapped to the absolute finger
      // position. This way picking a corner pebble and barely moving
      // doesn't make it leap across the screen. Lift keeps the stone
      // visible above the thumb.
      const dx = state.swipe.curX - state.swipe.startX;
      const dy = state.swipe.curY - state.swipe.startY;
      return { x: anchor.x + dx, y: anchor.y + dy - anchor.r * 1.4 };
    }
    const s = state.heap[i];
    if (s && s.returnT > 0) {
      const t = s.returnT / 0.22;             // 0 → 1 as we ease back
      const lx = s.releaseX != null ? s.releaseX : anchor.x;
      const ly = s.releaseY != null ? s.releaseY : anchor.y;
      return { x: anchor.x + (lx - anchor.x) * t, y: anchor.y + (ly - anchor.y) * t };
    }
    return { x: anchor.x, y: anchor.y };
  }

  // ── backing stones (i ≥ 1) first, so the ready pebble overdraws them ──
  for (let i = 1; i < state.heap.length; i++) {
    const s = state.heap[i];
    if (s.thrown) continue;
    const a = heapAnchor(i);
    const p = pickedDrawPos(i, a);
    const grown = i === pickedIdx ? 1.18 : 1.0;
    drawStone(p.x, p.y, a.r * grown, s.lump);
    if (i === pickedIdx) drawPickedHalo(p.x, p.y, a.r * grown);
  }

  // ── ready pebble (i === 0) ──
  const ready = state.heap[0];
  if (ready && !ready.thrown) {
    const a = heapAnchor(0);
    const p = pickedDrawPos(0, a);
    let rr = a.r;
    if (canThrow() && !state.swipe && !state.stone) {
      rr *= 1 + Math.sin(Date.now() * 0.0028) * 0.07;
    }
    if (pickedIdx === 0) rr *= 1.18;
    drawStone(p.x, p.y, rr, ready.lump);
    if (pickedIdx === 0) drawPickedHalo(p.x, p.y, rr);
  }
}

// Soft glow under the picked stone so the player sees what they're holding.
// Subtle by design — the *position* tracking the finger is the main signal.
function drawPickedHalo(x, y, r) {
  const g = ctx.createRadialGradient(x, y, r * 0.4, x, y, r * 2.4);
  g.addColorStop(0, 'rgba(255, 230, 170, 0.55)');
  g.addColorStop(1, 'rgba(255, 230, 170, 0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r * 2.4, 0, Math.PI * 2);
  ctx.fill();
}

// ---- HUD overlays ----

function drawTargetReticle() {
  if (!settings.reticle) return;
  const stage = state.stages[state.stageIdx];
  if (!stage || stage.done) return;
  const c = project(0, stage.def.h * 0.55, 14);
  if (!c.visible) return;
  const pulse = 1 + Math.sin(Date.now() * 0.004) * 0.06;
  const r = 30 * pulse;

  ctx.save();
  ctx.lineCap = 'round';
  const arm = 8;
  const corners = [[-r, -r, 1, 1], [r, -r, -1, 1], [-r, r, 1, -1], [r, r, -1, -1]];

  // Dark backing stroke first — gives the bright fill something to read
  // against on both bright sky and dark pillar.
  ctx.strokeStyle = 'rgba(20, 12, 4, 0.7)';
  ctx.lineWidth = 4;
  for (const [dx, dy, ax, ay] of corners) {
    ctx.beginPath();
    ctx.moveTo(c.sx + dx, c.sy + dy + arm * ay);
    ctx.lineTo(c.sx + dx, c.sy + dy);
    ctx.lineTo(c.sx + dx + arm * ax, c.sy + dy);
    ctx.stroke();
  }

  // Vivid gold-orange — pops against the sandstone pillar much more
  // clearly than the cream the corners used to be.
  ctx.strokeStyle = 'rgba(255, 190, 60, 1.0)';
  ctx.lineWidth = 2.2;
  for (const [dx, dy, ax, ay] of corners) {
    ctx.beginPath();
    ctx.moveTo(c.sx + dx, c.sy + dy + arm * ay);
    ctx.lineTo(c.sx + dx, c.sy + dy);
    ctx.lineTo(c.sx + dx + arm * ax, c.sy + dy);
    ctx.stroke();
  }
  ctx.restore();
}

// An irregular, lumpy pebble — not a perfect disc. `seed` fixes the lump
// pattern so a given stone keeps its shape; `rot` spins it (flying stones).
function drawStone(x, y, r, seed, rot) {
  seed = (seed || 0) + 1;
  const N = 11;
  const rnd = (k) => {
    const v = Math.sin(seed * 53.17 + k * 12.9898) * 43758.5453;
    return v - Math.floor(v);
  };

  ctx.save();
  ctx.translate(x, y);
  if (rot) ctx.rotate(rot);

  // irregular outline — vertices at jittered radii, smoothed through midpoints
  const pts = [];
  for (let i = 0; i < N; i++) {
    const ang = (i / N) * Math.PI * 2;
    const rad = r * (0.72 + rnd(i) * 0.36);
    pts.push([Math.cos(ang) * rad, Math.sin(ang) * rad * 0.92]);
  }
  ctx.beginPath();
  ctx.moveTo((pts[N - 1][0] + pts[0][0]) / 2, (pts[N - 1][1] + pts[0][1]) / 2);
  for (let i = 0; i < N; i++) {
    const c = pts[i], nx = pts[(i + 1) % N];
    ctx.quadraticCurveTo(c[0], c[1], (c[0] + nx[0]) / 2, (c[1] + nx[1]) / 2);
  }
  ctx.closePath();

  // shading — light from the upper-left, deep shadow lower-right
  const g = ctx.createRadialGradient(-r * 0.42, -r * 0.46, r * 0.1,
                                      r * 0.28,  r * 0.34, r * 1.7);
  g.addColorStop(0,    '#ded5c3');
  g.addColorStop(0.46, '#a89b83');
  g.addColorStop(1,    '#453c30');
  ctx.fillStyle = g;
  ctx.fill();
  ctx.strokeStyle = 'rgba(26,20,12,0.55)';
  ctx.lineWidth = Math.max(0.6, r * 0.11);
  ctx.stroke();

  if (r > 4) {
    // a faceting crease + a couple of pits for surface texture
    ctx.strokeStyle = 'rgba(40,32,22,0.34)';
    ctx.lineWidth = Math.max(0.5, r * 0.08);
    ctx.beginPath();
    ctx.moveTo(-r * 0.36, -r * 0.06);
    ctx.quadraticCurveTo(r * 0.02, r * 0.1, r * 0.44, -r * 0.2);
    ctx.stroke();
    ctx.fillStyle = 'rgba(40,32,22,0.22)';
    for (let i = 0; i < 2; i++) {
      const px = (rnd(i + 5) - 0.5) * r * 1.0;
      const py = (rnd(i + 8) - 0.2) * r * 0.7;
      ctx.beginPath();
      ctx.ellipse(px, py, r * 0.13, r * 0.09, 0.6, 0, Math.PI * 2);
      ctx.fill();
    }
    // highlight catch
    ctx.fillStyle = 'rgba(248,243,231,0.55)';
    ctx.beginPath();
    ctx.ellipse(-r * 0.36, -r * 0.38, r * 0.26, r * 0.16, -0.5, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawFlyingStone(s) {
  const pr = project(s.x, s.y, s.z);
  if (!pr.visible) return;
  for (let i = 0; i < s.trail.length; i++) {
    const t = s.trail[i];
    const tp = project(t.x, t.y, t.z);
    if (!tp.visible) continue;
    const a = (i + 1) / s.trail.length;
    ctx.fillStyle = `rgba(245,232,200,${a * 0.28})`;
    const tr = Math.max(1, s.r * tp.scale * a * 0.8);
    ctx.beginPath(); ctx.arc(tp.sx, tp.sy, tr, 0, Math.PI * 2); ctx.fill();
  }
  drawStone(pr.sx, pr.sy, Math.max(2, s.r * pr.scale), s.seed, s.spin);
}

function drawParticle(p) {
  const pr = project(p.x, p.y, p.z);
  if (!pr.visible) return;
  const a = Math.max(0, p.life / p.maxLife);
  const r = Math.max(1, p.r * pr.scale);
  if (p.kind === 'dust') {
    const g = ctx.createRadialGradient(pr.sx, pr.sy, 0, pr.sx, pr.sy, r * 2.2);
    g.addColorStop(0, p.color.replace(/[\d.]+\)$/, (a * 0.85).toFixed(2) + ')'));
    g.addColorStop(1, p.color.replace(/[\d.]+\)$/, '0)'));
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(pr.sx, pr.sy, r * 2.2, 0, Math.PI * 2); ctx.fill();
  } else {
    ctx.globalAlpha = a;
    ctx.fillStyle = p.color;
    ctx.beginPath(); ctx.arc(pr.sx, pr.sy, r, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
  }
}

function drawSplash(s) {
  const pr = project(s.x, s.y, s.z);
  if (!pr.visible) return;
  const a = Math.max(0, s.life / 0.6) * 0.5;
  ctx.fillStyle = `rgba(180, 140, 90, ${a})`;
  ctx.beginPath();
  ctx.ellipse(pr.sx, pr.sy, s.r * pr.scale, s.r * pr.scale * 0.35, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawMote(m) {
  const pr = project(m.x, m.y, m.z);
  if (!pr.visible) return;
  const depthAlpha = Math.max(0.12, Math.min(0.55, 1.2 / m.z));
  const flicker = 0.6 + 0.4 * Math.sin(m.phase);
  const a = depthAlpha * flicker * 0.55;
  const r = Math.max(0.6, m.size * pr.scale * 0.03);
  ctx.fillStyle = `rgba(255, 240, 210, ${a})`;
  ctx.beginPath(); ctx.arc(pr.sx, pr.sy, r, 0, Math.PI * 2); ctx.fill();
}

// Floating "+N" pts text at the spot a stone struck the pillar — value drops
// off with distance from the target centre, so this also gives the player
// visual feedback on aim quality. Drawn on top of everything else.
function drawScorePop(p) {
  const pr = project(p.x, p.y, p.z);
  if (!pr.visible) return;
  const a = Math.max(0, p.life / p.maxLife);
  // pop-in: scales up quickly at the start, then holds
  const popIn = Math.min(1, (p.maxLife - p.life) * 6 + 0.4);
  // Streak hits get a +30 % size bump and a slightly warmer gold —
  // matches the SCORE pill's .streak flourish.
  const streakBoost = p.streak ? 1.3 : 1.0;
  const size = Math.max(16, 30 * pr.scale * 0.045) * popIn * streakBoost;
  // colour intensity tracks the pts value — gold for bullseye, cream for edge
  const t = (p.pts - 1) / 9;                 // 0..1
  let r = Math.round(235 + (255 - 235) * t);
  let g = Math.round(220 + (210 - 220) * t);
  let b = Math.round(180 + (90  - 180) * t);
  if (p.streak) { r = 255; g = 210; b = 110; } // unmistakable warm gold
  ctx.save();
  ctx.font = `800 ${Math.round(size)}px -apple-system, "SF Pro Display", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = Math.max(2, size * 0.13);
  ctx.strokeStyle = `rgba(30, 18, 6, ${a * 0.65})`;
  ctx.fillStyle   = `rgba(${r}, ${g}, ${b}, ${a})`;
  const text = `+${p.pts}`;
  ctx.strokeText(text, pr.sx, pr.sy);
  ctx.fillText(text,   pr.sx, pr.sy);
  ctx.restore();
}

// Predicted-throw preview. The raw finger-swipe path is NOT drawn here —
// it's input, not feedback. What the player needs to see is the arc the
// stone will travel and where it will land:
//
//   - A smooth dashed arc from the pile up through the predicted path,
//     bright cream with a dark shadow stroke so it reads on any backdrop.
//   - An endpoint marker at the predicted impact: a gold ring if the arc
//     would hit the pillar, a soft red-amber ring if it would miss
//     (sky / ground / outside the target).
//
// Heavy use of state.wind / GRAVITY mirrors physics.update so the preview
// stays honest as wind shifts mid-aim.
function drawAimPreview() {
  if (!state.swipe) return;
  if (!settings.aimGuide) return;
  // Preview always returns a velocity — the player sees an arc the moment
  // they pick a stone, then it bends with the drag. The actual launch
  // path uses the stricter computeVelocity() gate.
  const v = previewVelocity(state.swipe);
  if (!v) return;
  const sw = state.swipe;
  // Visual hint that the current drag is below the throw threshold — the
  // arc dims so the player can tell "not yet committed" at a glance.
  const wouldThrow = v.dy >= 30 && v.dist >= 45;
  const alphaScale = wouldThrow ? 1.0 : 0.55;

  // Spawn x mirrors what input.js + physics.launchStone will actually use,
  // so the preview arc starts from the same world location the stone will
  // leave from when the player releases.
  const previewSpawnX = Math.max(-2.5, Math.min(2.5,
    (sw.curX - sw.startX) / (W * 0.25)
  ));

  // Simulate the flight in world space, stopping at first contact with
  // the pillar (HIT) or with the ground / out-of-bounds (MISS). Step
  // matches physics's effective dt scale loosely — fine enough for a
  // smooth visual arc.
  let sx = previewSpawnX, sy = EYE_H - 0.20, sz = 0.30;
  let vx = v.vx, vy = v.vy, vz = v.vz;
  const wall = currentWall();
  const pts = [{ x: sx, y: sy, z: sz }];
  const step = 0.035;
  let outcome = 'miss';      // 'hit' | 'miss'
  let endPt = null;
  for (let i = 0; i < 80; i++) {
    vy -= GRAVITY * step;
    vx += state.wind * step * 0.6;
    sx += vx * step; sy += vy * step; sz += vz * step;
    pts.push({ x: sx, y: sy, z: sz });

    // Pillar test — same egg-profile ellipse as physics.registerHit.
    if (wall && !wall.stage.done && sy > 0 && sy < wall.h) {
      const f = pillarRadiusFactor(sy / wall.h);
      const a = (wall.w / 2) * f;
      const b = (wall.d / 2) * f;
      const ex = (sx - wall.x) / a;
      const ez = (sz - wall.z) / b;
      if (ex * ex + ez * ez < 1) {
        outcome = 'hit';
        endPt = { x: sx, y: sy, z: sz };
        break;
      }
    }
    if (sy <= 0 || sz > 22 || Math.abs(sx) > 30 || sy > 40) {
      endPt = { x: sx, y: Math.max(0, sy), z: sz };
      break;
    }
  }
  if (!endPt) endPt = pts[pts.length - 1];

  // ── Arc — dashed poly-line in screen space, dark shadow + bright fill ──
  const screen = pts.map(p => project(p.x, p.y, p.z));
  ctx.save();
  ctx.lineCap  = 'round';
  ctx.lineJoin = 'round';
  ctx.setLineDash([7, 6]);

  // Shadow under-stroke so the arc reads on bright sky as well as sand.
  ctx.beginPath();
  for (let i = 0; i < screen.length; i++) {
    const s = screen[i];
    if (!s.visible) continue;
    if (i === 0) ctx.moveTo(s.sx, s.sy); else ctx.lineTo(s.sx, s.sy);
  }
  ctx.strokeStyle = `rgba(20, 12, 4, ${0.35 * alphaScale})`;
  ctx.lineWidth   = 4;
  ctx.stroke();

  // Bright fill on top — colour tracks outcome so the player gets a
  // tight read-ahead: gold = on-target, amber = miss.
  ctx.beginPath();
  for (let i = 0; i < screen.length; i++) {
    const s = screen[i];
    if (!s.visible) continue;
    if (i === 0) ctx.moveTo(s.sx, s.sy); else ctx.lineTo(s.sx, s.sy);
  }
  ctx.strokeStyle = outcome === 'hit'
    ? `rgba(255, 220, 130, ${0.75 * alphaScale})`   // warm gold — HIT
    : `rgba(255, 180, 140, ${0.65 * alphaScale})`;  // amber — MISS
  ctx.lineWidth   = 2;
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';
  ctx.restore();

  // ── Endpoint marker — concentric ring at the predicted impact ──
  const epr = project(endPt.x, endPt.y, endPt.z);
  if (epr.visible) {
    const pulse = 1 + Math.sin(Date.now() * 0.012) * 0.06;
    const r = Math.max(10, 14 * epr.scale * 0.045) * pulse;
    ctx.save();
    // Outcome-tinted ring (no heavy halo — kept light)
    ctx.lineWidth = 1.8;
    ctx.strokeStyle = outcome === 'hit'
      ? `rgba(255, 220, 130, ${0.85 * alphaScale})`
      : `rgba(255, 160, 120, ${0.70 * alphaScale})`;
    ctx.beginPath(); ctx.arc(epr.sx, epr.sy, r, 0, Math.PI * 2); ctx.stroke();
    // Tiny bullseye dot
    ctx.fillStyle = outcome === 'hit'
      ? `rgba(255, 240, 200, ${0.85 * alphaScale})`
      : `rgba(255, 200, 170, ${0.65 * alphaScale})`;
    ctx.beginPath(); ctx.arc(epr.sx, epr.sy, Math.max(2, r * 0.18), 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
}

// ---- Main draw call ----

function draw() {
  ctx.save();
  const shakeAmt = settings.reduceMotion ? 0 : state.shake;
  ctx.translate((Math.random() - 0.5) * shakeAmt, (Math.random() - 0.5) * shakeAmt);

  // Static background — sky / distant scape / ground / bridge crowd —
  // pre-rendered into bgCanvas in resize(); cheap to blit per frame.
  if (bgCanvas) {
    ctx.drawImage(bgCanvas, 0, 0, W, H);
  } else {
    const horY = horizonY();
    drawSky(horY); drawDistantScape(horY); drawGround(horY); drawBridgeCrowd();
  }

  const renderables = [];
  for (const s of state.stages) {
    if (s === state.stages[state.stageIdx] || s.done) {
      renderables.push({ z: 14, draw: () => drawWall(s, s === state.stages[state.stageIdx]) });
    }
  }
  for (const sp of state.splash)    renderables.push({ z: sp.z, draw: () => drawSplash(sp) });
  for (const p  of state.particles) renderables.push({ z: p.z,  draw: () => drawParticle(p) });
  if (!settings.reduceMotion) {
    for (const m of state.motes)    renderables.push({ z: m.z,  draw: () => drawMote(m) });
  }
  if (state.stone && state.stone.alive) {
    renderables.push({ z: state.stone.z, draw: () => drawFlyingStone(state.stone) });
  }
  renderables.sort((a, b) => b.z - a.z);
  for (const r of renderables) r.draw();

  if (state.running) drawTargetReticle();
  drawHero();
  // Trajectory preview removed by design — the picked stone tracking the
  // finger is the aim feedback; an arc on top of that was just noise.

  // Per-hit "+N" floats drawn on top of everything else for clear feedback.
  for (const p of state.scorePops) drawScorePop(p);

  if (state.floatMsgT > 0 && state.floatMsg) {
    const a = Math.min(1, state.floatMsgT / 400);
    ctx.font = '700 22px -apple-system, sans-serif';
    ctx.textAlign = 'center';
    // Dark outline + cream fill so the float reads against any background
    // (the sandstone sky used to wash out a pure-cream fill).
    ctx.lineWidth = 4;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = `rgba(30, 18, 6, ${a * 0.75})`;
    ctx.fillStyle   = `rgba(252, 240, 208, ${a})`;
    ctx.strokeText(state.floatMsg, W / 2, H * 0.36);
    ctx.fillText(state.floatMsg, W / 2, H * 0.36);
  }

  ctx.restore();
}

'use strict';

// The visible pebble heap is rendered at screen-centre (see drawHero in
// renderer.js), so the throw must originate from world x=0 too — otherwise
// a straight-up swipe drifts off the pillar centre because the physics
// spawn is offset from where the player sees the stone. The function name
// is kept for historical reasons (used to vary with settings.hand).
function spawnXForHand() {
  return 0;
}

function currentWall() {
  const s = state.stages[state.stageIdx];
  if (!s) return null;
  return { x: 0, y: 0, z: 14, ...s.def, stage: s };
}

function update(dt) {
  let effDt = dt;
  if (state.slowMoT > 0) {
    effDt = dt * state.slowMo;
    state.slowMoT -= dt;
    if (state.slowMoT <= 0) state.slowMo = 1.0;
  }

  if (state.cooldown > 0) state.cooldown--;

  if (settings.wind) {
    state.windT += effDt;
    state.wind = Math.sin(state.windT * 0.5) * 1.4;
  } else {
    state.wind = 0;
  }

  if (state.stone && state.stone.alive) {
    const s = state.stone;

    s.vy -= GRAVITY * effDt;
    s.vx += state.wind * effDt * 0.6;
    s.x += s.vx * effDt;
    s.y += s.vy * effDt;
    s.z += s.vz * effDt;
    s.spin += effDt * 12;
    s.trail.push({ x: s.x, y: s.y, z: s.z });
    if (s.trail.length > 34) s.trail.shift();

    const wall = currentWall();
    if (wall && !wall.stage.done) {
      // Egg-profiled pillar — the hit ellipse widens/narrows with height to
      // match the rendered silhouette (see pillarRadiusFactor).
      const f = pillarRadiusFactor(s.y / wall.h);
      const a = (wall.w / 2) * f + s.r;
      const b = (wall.d / 2) * f + s.r;
      const ex = (s.x - wall.x) / a;
      const ez = (s.z - wall.z) / b;
      if (ex * ex + ez * ez < 1 && s.y > 0 && s.y < wall.h) {
        registerHit(s, wall);
        return;
      }
    }

    if (s.y <= 0) {
      s.alive = false;
      spawnDust(s.x, 0, s.z);
      registerMiss(s);
      return;
    }

    if (s.z > 22 || Math.abs(s.x) > 30 || s.y > 40) {
      s.alive = false;
      registerMiss(s);
      return;
    }
  } else if (state.stone && !state.stone.alive) {
    state.stone = null;
  }

  for (const s of state.stages) if (s.wobble > 0) s.wobble *= 0.88;
  if (state.shake > 0) state.shake *= 0.85;

  for (const p of state.particles) {
    p.vy -= GRAVITY * effDt * 0.4;
    p.x += p.vx * effDt; p.y += p.vy * effDt; p.z += p.vz * effDt;
    p.life -= effDt;
  }
  state.particles = state.particles.filter(p => p.life > 0);
  for (const d of state.splash) { d.life -= effDt; d.r += effDt * 2; }
  state.splash = state.splash.filter(d => d.life > 0);

  for (const p of state.scorePops) { p.life -= effDt; p.y += effDt * 1.1; }
  state.scorePops = state.scorePops.filter(p => p.life > 0);

  if (!settings.reduceMotion) {
    for (const m of state.motes) {
      m.x += (m.vx + state.wind * 0.3) * effDt;
      m.y += m.vy * effDt;
      m.z += m.vz * effDt;
      m.phase += effDt * 1.2;
      if (m.y < 0.25) { m.vy = Math.abs(m.vy); m.y = 0.25; }
      if (m.y > 3.8)  { m.vy = -Math.abs(m.vy); }
      if (Math.abs(m.x) > 8) m.vx *= -1;
      if (m.z < 2 || m.z > 16) m.vz *= -1;
    }
  }

  if (state.floatMsgT > 0) state.floatMsgT -= effDt * 1000;

  // Tick the per-heap-stone return animation (set when the player picks
  // but releases without enough swipe to throw — see input.js onUp).
  if (state.heap && state.heap.length) {
    for (const s of state.heap) {
      if (s.returnT > 0) s.returnT = Math.max(0, s.returnT - effDt);
    }
  }
}

function registerHit(s, w) {
  s.alive = false;
  const stage = w.stage;
  stage.hits++;
  stage.wobble = 1;
  stage.throws.push({ hit: true });
  state.totalHits++;
  state.streak++;
  if (state.streak > state.bestStreak) state.bestStreak = state.streak;
  state.shake = settings.reduceMotion ? 0 : 8;
  // Streak flourish — once the player is on a 3+ hit roll, brief slow-mo
  // amplifies the next hit. The last-stone branch in launchStone() sets a
  // longer slow-mo, so only apply this when that one isn't already armed.
  if (
    state.streak >= 3 &&
    state.slowMoT <= 0 &&
    !settings.reduceMotion
  ) {
    state.slowMo  = 0.65;
    state.slowMoT = 0.45;
  }
  spawnHitParticles(s.x, s.y, s.z);
  settlePebble(stage, w, s.x, s.y);

  // Score by distance from the target centre (the reticle bullseye at
  // x=0, y=h*0.55). A hit inside the bull radius gets the max; beyond
  // that, points fall off linearly to 1 at the edge of the scoring zone.
  const targetY = w.h * 0.55;
  const dx = s.x;
  const dy = s.y - targetY;
  const d  = Math.sqrt(dx * dx + dy * dy);
  const bullR = 0.6;
  const maxR  = w.h * 0.55;
  const MAX_PTS = 10;
  let pts;
  if (d <= bullR)      pts = MAX_PTS;
  else if (d >= maxR)  pts = 1;
  else pts = Math.max(1, Math.round(MAX_PTS - (MAX_PTS - 1) * (d - bullR) / (maxR - bullR)));
  state.totalPoints += pts;
  state.scorePops.push({
    x: s.x, y: s.y, z: s.z,
    pts, life: 1.1, maxLife: 1.1,
    streak: state.streak >= 3,
  });

  Sfx.hit(); haptic([0, 55, 30, 55], 'hit');
  renderProgress(); popLastDot(); updateLiveScore(); renderPoints();
  // Game-specific fields go inside `data` — the shared GameEventDto on the
  // backend is { type, t, data? }. The event log is a session audit trail;
  // the score the player saw is sent separately as clientScore.
  state.events.push({
    type: 'hit',
    t: Date.now() - state.sessionStartT,
    data: {
      stage: stage.key,
      stoneNum: stage.throws.length,
      hitX: +s.x.toFixed(3),
      hitY: +s.y.toFixed(3),
    },
  });
  afterThrow();
}

function registerMiss(s) {
  const stage = state.stages[state.stageIdx];
  stage.throws.push({ hit: false });
  state.streak = 0;
  const wall = currentWall();
  if (wall && Math.abs(s.x) < wall.w * 0.9 && s.z < wall.z && s.z > wall.z - 4) {
    stage.landedPebbles.push({
      x: s.x, z: Math.max(s.z, wall.z - wall.d / 2 - 1.8),
      size: 0.055 + Math.random() * 0.03,
      tint: 0.75 + Math.random() * 0.25,
    });
  }
  Sfx.miss(); haptic([0, 18], 'miss');
  renderProgress(); popLastDot(); updateLiveScore();
  state.events.push({ type: 'miss', t: Date.now() - state.sessionStartT, data: { stage: stage.key, stoneNum: stage.throws.length } });
  afterThrow();
}

function settlePebble(stage, wall, hitX, hitY) {
  // Hit mark on the pillar's front face — keep y so the renderer can paint a
  // pale impact spot at the actual strike height (per design).
  const clampedY = Math.max(0.4, Math.min(wall.h - 0.3, hitY != null ? hitY : wall.h * 0.5));
  // Match the egg profile at the strike height so the mark sits on the face.
  const f = pillarRadiusFactor(clampedY / wall.h);
  const a = (wall.w / 2) * f;
  const b = (wall.d / 2) * f;
  const clampedX = Math.max(-a * 0.92, Math.min(a * 0.92, hitX));
  const frontZ = wall.z - b * Math.sqrt(Math.max(0, 1 - (clampedX / a) * (clampedX / a)));
  stage.landedPebbles.push({
    x: clampedX + (Math.random() - 0.5) * 0.2,
    y: clampedY,
    z: frontZ - 0.04,
    size: 0.07 + Math.random() * 0.04,
    tint: 0.85 + Math.random() * 0.2,
  });
}

function afterThrow() {
  const stage = state.stages[state.stageIdx];
  if (stage.throws.length >= STONES_PER_JAMRAH) {
    stage.done = true;
    state.events.push({ type: 'stage_complete', t: Date.now() - state.sessionStartT, data: { stage: stage.key, hits: stage.hits } });
    sendToHost('game:stage_complete', { game: 'rami', stage: stage.key, hits: stage.hits });
    setTimeout(advanceStage, 650);
  }
}

function spawnHitParticles(x, y, z) {
  const dustN  = settings.reduceMotion ? 6  : 14;
  const chipsN = settings.reduceMotion ? 2  : 5;

  for (let i = 0; i < dustN; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = 0.8 + Math.random() * 2.2;
    state.particles.push({
      x, y, z,
      vx: Math.cos(a) * sp * 0.35,
      vy: Math.random() * 1.2 + 0.3,
      vz: Math.sin(a) * sp * 0.2 - 0.4,
      life: 0.9 + Math.random() * 0.7, maxLife: 1.6,
      color: Math.random() < 0.5 ? 'rgba(220, 195, 155, 0.9)' : 'rgba(180, 145, 100, 0.85)',
      r: 0.08 + Math.random() * 0.1,
      kind: 'dust',
    });
  }

  for (let i = 0; i < chipsN; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = 3 + Math.random() * 4;
    state.particles.push({
      x, y, z,
      vx: Math.cos(a) * sp * 0.4,
      vy: Math.sin(a * 1.3) * sp * 0.4 + 2.2,
      vz: Math.sin(a) * sp * 0.3 - 0.6,
      life: 0.5 + Math.random() * 0.4, maxLife: 0.9,
      color: '#3a2e22',
      r: 0.03 + Math.random() * 0.03,
      kind: 'chip',
    });
  }
}

function spawnDust(x, y, z) {
  state.splash.push({ x, y, z, r: 0.3, life: 0.6 });
}

function showFloat(txt, ms) {
  state.floatMsg = txt;
  state.floatMsgT = ms;
}

function launchStone(v, pickedIndex, spawnXOffset) {
  // The visible pile and the in-flight stone must be the same pebble: take
  // the lump seed from the heap entry the player picked, then flip its
  // `thrown` flag so drawHero stops rendering it in the pile.
  let seed;
  if (pickedIndex != null && pickedIndex >= 0 && state.heap[pickedIndex]) {
    const picked = state.heap[pickedIndex];
    picked.thrown = true;
    seed = picked.lump;
  } else {
    seed = (Math.random() * 1000) | 0;
  }
  // Spawn x is offset by where the player had the stone (input.js sets
  // this from the drag delta) — clamped so an extreme drag can't launch
  // from impossibly far out.
  const spawnX = Math.max(-2.5, Math.min(2.5, spawnXOffset || 0));
  state.stone = {
    x: spawnX, y: EYE_H - 0.20, z: 0.30,
    vx: v.vx, vy: v.vy, vz: v.vz,
    r: 0.085,
    trail: [],
    spin: 0,
    seed,
    alive: true,
  };
  state.stonesLeft--;
  state.totalThrows++;
  state.takbirCount++;
  state.cooldown = 10;
  Sfx.throw_(); haptic([0, 25], 'throw');
  flashTakbir();
  renderProgress();
  // stoneNum is the 1-based stone index WITHIN the current stage (1..7) — the
  // server validates it per-stage against [1, maxStones], so a global counter
  // would fail for stages 2+ in multi-pillar modes.
  state.events.push({ type: 'throw', t: Date.now() - state.sessionStartT, data: { stage: state.stages[state.stageIdx]?.key, stoneNum: STONES_PER_JAMRAH - state.stonesLeft, vx: +v.vx.toFixed(3), vy: +v.vy.toFixed(3), vz: +v.vz.toFixed(3), power: +v.power.toFixed(3) } });

  if (state.stonesLeft === 0 && !settings.reduceMotion) {
    state.slowMo = 0.55; state.slowMoT = 2.2;
  }
}

'use strict';

function evtPos(e) {
  const rect = canvas.getBoundingClientRect();
  const t = e.touches ? e.touches[0] : e;
  return { x: t.clientX - rect.left, y: t.clientY - rect.top, t: performance.now() };
}

function canThrow() {
  return state.running && !state.paused && !state.stone && state.stonesLeft > 0 && state.cooldown <= 0;
}

// Nearest un-thrown heap stone to (px, py) within a forgiving radius.
// Touch targets on mobile are imprecise, so we snap to the closest stone
// inside R = max(stone.r * 2.4, 36 px); anything further away is treated as
// "not a pick" so a tap on empty canvas is a no-op rather than a stray throw.
function pickHeapStone(px, py) {
  if (!state.heap || state.heap.length === 0) return -1;
  let best = -1, bestD2 = Infinity;
  for (let i = 0; i < state.heap.length; i++) {
    if (state.heap[i].thrown) continue;
    const a = heapAnchor(i);
    const R = Math.max(a.r * 2.4, 36);
    const dx = px - a.x, dy = py - a.y;
    const d2 = dx * dx + dy * dy;
    if (d2 <= R * R && d2 < bestD2) {
      bestD2 = d2;
      best = i;
    }
  }
  return best;
}

// Shared velocity math — used by both the launch path (computeVelocity)
// and the preview path (previewVelocity). The only difference: the launch
// path refuses small/slow drags (so an idle tap can't throw), the preview
// path always returns a value so the arc is visible from the moment the
// player picks a stone.
function _velFromSwipe(sw) {
  const pts = sw.points;
  if (pts.length < 2) {
    return { vx: 0, vy: 3, vz: 8, dy: 0, dist: 0, power: 0 };
  }
  const first = pts[0], last = pts[pts.length - 1];
  const dt = Math.max(16, last.t - first.t);
  const dx = last.x - first.x;
  const dy = first.y - last.y;  // upward positive
  const dist = Math.hypot(dx, dy);
  const safeDist = Math.max(1, dist);
  const speedPxPerMs = dist / dt;
  const vertFactor  = Math.max(0, dy) / H;
  const horizFactor = dx / W;

  const vz = Math.min(28, 8 + vertFactor * 28 + speedPxPerMs * 2);
  const vy = 3 + vertFactor * 6 + speedPxPerMs * 0.8;
  // Lateral response was weak — a half-screen sideways drag barely
  // shifted vx, so trajectories all bent toward the pillar. Boosted so
  // swiping at an angle visibly steers the throw.
  const vx = horizFactor * 16 + (speedPxPerMs * dx / safeDist) * 3.0;
  const power = Math.max(0, Math.min(1, (vz - 8) / 18));
  return { vx, vy, vz, dy, dist, power };
}

function computeVelocity(sw) {
  const v = _velFromSwipe(sw);
  // Throw threshold — kept here so the launch path stays gated, but the
  // preview can ignore it and always paint an arc.
  if (v.dy < 30 || v.dist < 45) return null;
  return v;
}

// Like computeVelocity, but always returns a velocity — used for the
// trajectory preview so the player sees where ANY swipe-in-progress
// would send the stone (including a not-yet-committed tiny drag).
function previewVelocity(sw) {
  return _velFromSwipe(sw);
}

function onDown(e) {
  if (!canThrow()) return;
  const p = evtPos(e);
  // The player must touch a pebble in the pile — empty-canvas taps don't
  // start a throw any more. Forgiving radius is inside pickHeapStone().
  const pickedIndex = pickHeapStone(p.x, p.y);
  if (pickedIndex < 0) return;
  e.preventDefault();
  Sfx.resume();
  state.swipe = {
    startX: p.x, startY: p.y, startT: p.t,
    curX: p.x, curY: p.y, points: [p],
    pickedIndex,
  };
  hintEl.classList.add('hidden');
}

function onMove(e) {
  if (!state.swipe) return;
  e.preventDefault();
  const p = evtPos(e);
  state.swipe.curX = p.x; state.swipe.curY = p.y;
  state.swipe.points.push(p);
  const cutoff = p.t - 130;
  while (state.swipe.points.length > 2 && state.swipe.points[0].t < cutoff) {
    state.swipe.points.shift();
  }
}

function onUp(e) {
  if (!state.swipe) return;
  if (e && e.preventDefault) e.preventDefault();
  const sw = state.swipe; state.swipe = null;
  const v = computeVelocity(sw);
  if (!v) {
    // Picked but not enough swipe to throw — drop the stone back into the
    // pile with a brief return animation (drawHero reads returnT). This
    // makes "I picked but didn't commit" feel deliberate, not glitchy.
    const s = state.heap && state.heap[sw.pickedIndex];
    if (s && !s.thrown) {
      s.returnT  = 0.22;
      s.releaseX = sw.curX;
      s.releaseY = sw.curY;
    }
    // Bring the hint back so the player knows what to do next.
    if (typeof hintEl !== 'undefined' && hintEl) hintEl.classList.remove('hidden');
    return;
  }
  // Spawn x follows the drag — full-screen drag ≈ ±2 m lateral spawn, so
  // a stone clearly leaves the hand-position the player is pointing from.
  const spawnX = (sw.curX - sw.startX) / (W * 0.25);
  launchStone(v, sw.pickedIndex, spawnX);
}

canvas.addEventListener('mousedown',   onDown);
canvas.addEventListener('mousemove',   onMove);
window.addEventListener('mouseup',     onUp);
canvas.addEventListener('touchstart',  onDown,  { passive: false });
canvas.addEventListener('touchmove',   onMove,  { passive: false });
canvas.addEventListener('touchend',    onUp,    { passive: false });
canvas.addEventListener('touchcancel', onUp,    { passive: false });

'use strict';

// ─── DOM refs ──────────────────────────────────────────────
const hud          = document.getElementById('hud');
const progressEl   = document.getElementById('progress');
const pauseBtn     = document.getElementById('pauseBtn');
const takbirEl     = document.getElementById('takbir');
const hintEl       = document.getElementById('hint');

// The centred pillar-name banner was removed from the HUD (it crowded
// the target). Stage identity now surfaces via the in-canvas showFloat
// at game start and between Tashrīq stages.
const stonesPillEl = document.getElementById('stonesPill');
const stonesCountEl= document.getElementById('stonesCount');
const pointsPillEl = document.getElementById('pointsPill');
const pointsCountEl= document.getElementById('pointsCount');

const startOv      = document.getElementById('startOverlay');
const endOv        = document.getElementById('endOverlay');
const sheet        = document.getElementById('sheet');
const aboutOv      = document.getElementById('aboutOverlay');
const transitionOv = document.getElementById('transitionOverlay');

const endHitsEl    = document.getElementById('endHits');
const endTotalEl   = document.getElementById('endTotal');
const endThrowsEl  = document.getElementById('endThrows');
const endAccEl     = document.getElementById('endAccuracy');
const endStreakEl  = document.getElementById('endStreak');

const transitionFromEl = document.getElementById('transitionFromName');
const transitionToEl   = document.getElementById('transitionToName');

// ─── Progress pills (Ūlā · Wusṭā · ʿAqaba) ─────────────────

function renderProgress() {
  progressEl.innerHTML = '';
  if (!state.stages.length) return;

  state.stages.forEach((s, i) => {
    const step = document.createElement('div');
    step.className = 'step';
    if (s.done) step.classList.add('done');
    if (i === state.stageIdx && !s.done) step.classList.add('active');

    const dot = document.createElement('div');
    dot.className = 'dot';

    const name = document.createElement('div');
    name.className = 'name';
    name.textContent = s.def.short;

    step.appendChild(dot);
    step.appendChild(name);
    progressEl.appendChild(step);
  });

  renderHud();
}

// ─── Top-banner + stones-pill ──────────────────────────────

function renderHud() {
  const stage = state.stages[state.stageIdx];
  if (!stage) {
    stonesPillEl.classList.add('hidden');
    pointsPillEl && pointsPillEl.classList.add('hidden');
    return;
  }
  stonesPillEl.classList.remove('hidden');
  pointsPillEl && pointsPillEl.classList.remove('hidden');

  const remaining = STONES_PER_JAMRAH - stage.throws.length;
  stonesCountEl.textContent = remaining;
  renderPoints();
}

// Updates the live-score pill from state.totalPoints. Called whenever points
// change (per hit) — kept separate so physics.js can refresh it without
// re-rendering the rest of the HUD. Also toggles the .streak modifier so
// the pill pulses harder on a hot streak.
function renderPoints() {
  if (!pointsCountEl) return;
  pointsCountEl.textContent = state.totalPoints || 0;
  if (pointsPillEl) {
    const streak = (state.streak || 0) >= 3;
    pointsPillEl.classList.toggle('streak', streak);
    pointsPillEl.classList.remove('pop');
    void pointsPillEl.offsetWidth;
    pointsPillEl.classList.add('pop');
    setTimeout(() => pointsPillEl.classList.remove('pop'), 240);
  }
}

// Pulse the stones pill briefly when the count changes.
function pulseStonesPill() {
  if (!stonesPillEl) return;
  stonesPillEl.classList.remove('pop');
  void stonesPillEl.offsetWidth;
  stonesPillEl.classList.add('pop');
  setTimeout(() => stonesPillEl.classList.remove('pop'), 240);
}

// Compatibility shims for physics.js — it still calls these names on each throw.
function popLastDot()      { pulseStonesPill(); }
function updateLiveScore() { /* the stones pill is the live score */ }

// ─── Takbīr glyph ───────────────────────────────────────────

function flashTakbir() {
  if (!settings.takbir) return;
  // Text is in the HTML (Arabic + transliteration). Just re-trigger the
  // entrance animation by removing and re-adding the `show` class.
  takbirEl.classList.remove('show');
  void takbirEl.offsetWidth;
  takbirEl.classList.add('show');
}

// ─── Pillar transition overlay ─────────────────────────────
function showPillarTransition(fromDef, toDef, onContinue) {
  if (!fromDef || !toDef) { onContinue && onContinue(); return; }
  transitionFromEl.textContent = fromDef.name;
  transitionToEl.textContent   = toDef.name;
  transitionOv.classList.remove('hidden');

  const continueBtn = document.getElementById('transitionContinueBtn');
  const handler = () => {
    continueBtn.removeEventListener('click', handler);
    transitionOv.classList.add('hidden');
    // Re-surface the next pillar's name on the canvas itself — the
    // persistent top-banner is gone, so the float is now the only place
    // the player sees the stage name during gameplay.
    showFloat(toDef.name, 1500);
    onContinue && onContinue();
  };
  continueBtn.addEventListener('click', handler);
}

// ─── About modal ───────────────────────────────────────────
document.getElementById('aboutBtn').addEventListener('click', () => {
  aboutOv.classList.remove('hidden');
});
document.getElementById('aboutCloseBtn').addEventListener('click', () => {
  aboutOv.classList.add('hidden');
});
aboutOv.addEventListener('click', (e) => {
  if (e.target === aboutOv) aboutOv.classList.add('hidden');
});

// ─── Mode buttons (day cards) ──────────────────────────────
document.querySelectorAll('.mode-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    if (btn.disabled) return;
    Sfx.resume();
    // Spin only the card the user tapped — the other dims — so the press
    // never reads as both cards being selected.
    setDayPickerLoading(btn);
    startGame(btn.dataset.mode);
  });
});

// ─── HUD buttons ───────────────────────────────────────────
// Leaving the game is handled by the native app's back button — the web
// exposes no back/exit affordance. exitToMenu() still exists for the
// native game:exit lifecycle event (see wallet.js).
pauseBtn.addEventListener('click', () => pauseGame());
document.getElementById('resumeBtn').addEventListener('click', () => resumeGame());

// ─── End screen buttons ────────────────────────────────────
document.getElementById('againBtn').addEventListener('click', () => startGame(state.modeKey || 'tashreeq'));
document.getElementById('claimBtn').addEventListener('click', () => claimPoints());
document.getElementById('doneBtn').addEventListener('click', () => {
  snapshot.clear();
  endOv.classList.add('hidden');
  // Same invariant as exitToMenu — the start overlay must always show
  // clickable day-cards.
  clearDayPickerLoading();
  startOv.classList.remove('hidden');
  sendToHost('rami:exit');
});

// ─── Settings sheet toggles ────────────────────────────────
document.querySelectorAll('.toggle').forEach(toggle => {
  const key = toggle.dataset.key;
  function refresh() { toggle.classList.toggle('on', !!settings[key]); }
  refresh();
  toggle.addEventListener('click', () => {
    settings[key] = !settings[key];
    saveSettings();
    refresh();
  });
});

'use strict';

// Build the pile of pebbles the player picks from. One entry per stone the
// stage will spend — index 0 is the "ready" pebble at the front of the pile
// (larger, pulses), 1..N-1 are the loose backing stones behind it. The
// `lump` field is the seed handed to drawStone() so each pebble keeps a
// stable shape. Screen positions are resolved by heapAnchor(i) at draw
// time — the heap itself is resolution-free so a resize doesn't have to
// touch it.
function buildHeap() {
  const N = STONES_PER_JAMRAH;
  state.heap = [];
  for (let i = 0; i < N; i++) {
    state.heap.push({
      isReady: i === 0,
      lump:    i === 0 ? 1 : i + 6,
      thrown:  false,
    });
  }
}

async function startGame(modeKey) {
  // If a previous logged-in session is still hanging around (player skipped
  // claim and started a new round), abandon it server-side. Guest sessions
  // have no backend record so there's nothing to abandon.
  if (state.sessionId) {
    api.abandonSession(state.sessionId).catch(() => {});
    state.sessionId = null;
  }

  // Logged-in users get the full lifecycle: session/start now, session/complete
  // at game end. Guests skip the backend entirely and use session/submit at
  // claim time.
  if (state.isGuest === false) {
    // The picked card is already spinning (set by the day-card click handler).
    let session;
    try {
      session = await api.startSession(modeKey);
    } catch (e) {
      clearDayPickerLoading();
      showFloat('Could not start. Try again.', 2000);
      return;
    }
    state.sessionId = session.sessionId;
    if (session.balance != null) {
      wallet.balance  = session.balance;
      wallet.currency = session.currency;
      renderWallet();
    }
  } else {
    state.sessionId = null;
  }
  // Game is starting — reset the picker so it's clean next time it's shown.
  clearDayPickerLoading();

  // Starting a new game means the boot-time snapshot resume is no longer
  // relevant — clear the flag so a later auth:ready won't auto-fire claim
  // against a stale CTA from the previous game's restore.
  state.restoredFromSnapshot = false;

  state.sessionStartT = Date.now();
  state.events        = [];

  state.modeKey = modeKey;
  const stageKeys = MODES[modeKey].stages;
  state.stages = stageKeys.map(k => {
    let def = JAMARAT[k];
    // Day of Naḥr stones only Jamrat al-ʿAqaba — the largest pillar. Rendered
    // alone it fills more of the view than the Tashrīq pillars do. Keep its
    // ʿAqaba identity (name/Arabic) but render it at the ʿUlā footprint so the
    // single-day POV matches the preferred three-pillar view. Physics reads
    // the same def, so the hit test stays in agreement with the visual.
    if (modeKey === 'nahr') {
      def = { ...def, w: JAMARAT.ula.w, d: JAMARAT.ula.d, h: JAMARAT.ula.h };
    }
    return {
      key: k, def,
      hits: 0, throws: [], done: false, wobble: 0,
      landedPebbles: [],
    };
  });
  state.stageIdx   = 0;
  state.stonesLeft = STONES_PER_JAMRAH;
  state.totalHits  = 0; state.totalThrows = 0;
  state.streak     = 0; state.bestStreak  = 0;
  state.takbirCount     = 0;
  state.running    = true; state.paused = false;
  state.stone      = null; state.particles = []; state.splash = [];
  state.scorePops  = []; state.totalPoints = 0;
  state.cooldown   = 0; state.slowMo = 1; state.slowMoT = 0;
  state.firstThrowDone = false;
  buildHeap();

  startOv.classList.add('hidden');
  endOv.classList.add('hidden');
  hud.classList.remove('hidden');
  stonesPillEl && stonesPillEl.classList.remove('hidden');
  pointsPillEl && pointsPillEl.classList.remove('hidden');
  hintEl.classList.remove('hidden');

  renderProgress();
  renderPoints();
  updateLiveScore();
  showFloat('Begin: ' + state.stages[0].def.name, 1600);
  sendToHost('game:start', { game: 'rami', mode: modeKey });
}

async function endGame() {
  state.running = false;

  const acc      = state.totalThrows > 0 ? Math.round((state.totalHits / state.totalThrows) * 100) : 0;
  const totalAvailable = state.stages.length * STONES_PER_JAMRAH;

  endHitsEl.textContent   = state.totalHits;
  if (endTotalEl)  endTotalEl.textContent  = totalAvailable;
  if (endThrowsEl) endThrowsEl.textContent = state.totalThrows;
  endAccEl.textContent    = acc + '%';
  endStreakEl.textContent = state.bestStreak;

  hud.classList.add('hidden');
  stonesPillEl && stonesPillEl.classList.add('hidden');
  pointsPillEl && pointsPillEl.classList.add('hidden');
  endOv.classList.remove('hidden');
  Sfx.done(); haptic([0, 40, 80, 40, 80, 40], 'success');

  // Two paths:
  //   - Logged-in (state.sessionId set): auto-claim via /session/complete.
  //   - Guest: show the "Sign in to claim" CTA. Tapping it triggers native
  //     login via the bridge auth-retry, then submits via /session/submit.
  if (state.sessionId) {
    // Show the player's actual earned score immediately — what they saw rack
    // up during play. The server-side claim happens in the background; we no
    // longer overwrite this with whatever the server returns (which can be a
    // flat / placeholder value).
    document.getElementById('endPoints').textContent = `+${state.totalPoints} pts`;
    await claimPoints();
  } else {
    showClaimCta();
  }
}

// Branches on whether a backend session already exists:
//   - state.sessionId set → POST /game/session/complete (logged-in lifecycle)
//   - state.sessionId null → POST /game/session/submit  (one-shot guest claim)
//
// For known guests, trigger native login proactively via the bridge so we
// don't waste a guaranteed-401 round-trip. The auth-retry inside api.js
// still handles the case where a logged-in user's cookie has expired
// (auto-recovery on 401 from complete/submit).
async function claimPoints() {
  const claimBtn  = document.getElementById('claimBtn');
  const endPoints = document.getElementById('endPoints');
  if (claimBtn) claimBtn.disabled = true;

  if (state.isGuest) {
    // Snapshot the unsent event log + display stats BEFORE handing off to
    // native OAuth. Survives a process kill between auth:login_required and
    // a successful submit — the boot path in wallet.js restores #endOv on
    // next launch (see snapshot.js + maybeResumeFromSnapshot).
    try { snapshot.save(snapshot.buildPostGame(state)); } catch (_) {}

    if (endPoints) endPoints.classList.add('hidden');
    try {
      await api.requestLogin();
    } catch (e) {
      // Login cancelled or failed — toast + leave the CTA visible for retry.
      // Snapshot stays so the user can reopen later and retry the claim.
      showFloat('Login cancelled — tap to claim when ready.', 2200);
      trackEvent('rami_login_cancelled', { mode: state.modeKey });
      showClaimCta();
      if (claimBtn) claimBtn.disabled = false;
      return;
    }
  }

  if (endPoints) endPoints.textContent = `+${state.totalPoints} pts`;
  if (endPoints) endPoints.classList.remove('hidden');

  try {
    const result = state.sessionId
      ? await api.completeSession(state.sessionId, state.events, state.totalPoints)
      : await api.submitSession(state.modeKey, state.events, state.totalPoints);

    // First successful authed call also flips us out of guest mode.
    state.isGuest = false;
    document.getElementById('wallet-pill')?.classList.remove('hidden');
    // /complete and /submit don't return a balance — the wallet credit is
    // enqueued server-side (walletStatus: 'pending'). Optimistically add the
    // points earned; the next /game/balance probe reconciles to the source
    // of truth.
    if (typeof result.pointsEarned === 'number') {
      wallet.balance = (wallet.balance || 0) + result.pointsEarned;
    }
    if (result.currency) wallet.currency = result.currency;
    renderWallet();

    // Successful claim — restore the label/value layout (cancel CTA mode).
    // Display the player's local score (matches what they saw in the score
    // pill), NOT result.pointsEarned which can be a flat backend value.
    document.getElementById('endPointsRow')?.classList.remove('cta');
    endPoints.textContent = `+${state.totalPoints} pts`;
    endPoints.classList.remove('hidden');
    if (claimBtn) claimBtn.classList.add('hidden');

    snapshot.clear();
    state.sessionId = null;
    sendToHost('game:complete', { game: 'rami', mode: state.modeKey });
    trackEvent('rami_points_claimed', { mode: state.modeKey, pointsEarned: result.pointsEarned });
  } catch (e) {
    if (e.status === 401 || e.status === 403) {
      // Login was cancelled or failed — toast + leave the claim CTA in place.
      showFloat('Login cancelled — tap to claim when ready.', 2200);
      showClaimCta();
    } else {
      endPoints.textContent = 'Error — try again';
      endPoints.classList.remove('hidden');
      if (claimBtn) claimBtn.classList.add('hidden');
    }
  } finally {
    if (claimBtn) claimBtn.disabled = false;
  }
}

function showClaimCta() {
  const row      = document.getElementById('endPointsRow');
  const claimBtn = document.getElementById('claimBtn');
  const endPoints = document.getElementById('endPoints');
  // Drop the "Points earned · +0 pts" label/value while the user hasn't
  // signed in — there's nothing to label. The CTA button takes the row alone.
  if (row)       row.classList.add('cta');
  if (endPoints) endPoints.classList.add('hidden');
  if (claimBtn)  claimBtn.classList.remove('hidden');
  trackEvent('rami_claim_cta_shown', { mode: state.modeKey });
}

// Re-hydrate post-game state from a localStorage snapshot. Repaints #endOv
// so the user lands back on the same screen if the WebView/process was wiped
// between auth:login_required and a successful claim. Called once from the
// boot path in wallet.js's maybeResumeFromSnapshot.
function restoreFromSnapshot(snap) {
  // state.isGuest is intentionally NOT overwritten here — initWallet has just
  // set it authoritatively from the /game/balance probe. If the user came back
  // signed in, isGuest=false and claimPoints will skip requestLogin and go
  // straight to submitSession. If they're still a guest, isGuest=true and the
  // claim CTA path runs normally.
  state.modeKey      = snap.mode;
  state.events       = Array.isArray(snap.events) ? snap.events : [];
  state.totalHits    = snap.stats.totalHits   | 0;
  state.totalThrows  = snap.stats.totalThrows | 0;
  state.bestStreak   = snap.stats.bestStreak  | 0;
  state.totalPoints  = snap.stats.totalPoints | 0;
  state.sessionId    = snap.sessionId || null;
  state.restoredFromSnapshot = true;

  endHitsEl.textContent   = state.totalHits;
  if (endThrowsEl) endThrowsEl.textContent = state.totalThrows;
  if (endTotalEl) {
    const mode = MODES[snap.mode];
    const totalAvailable = (mode ? mode.stages.length : 1) * STONES_PER_JAMRAH;
    endTotalEl.textContent = totalAvailable;
  }
  endAccEl.textContent    = (snap.stats.accuracy | 0) + '%';
  endStreakEl.textContent = state.bestStreak;

  startOv.classList.add('hidden');
  hud.classList.add('hidden');
  stonesPillEl && stonesPillEl.classList.add('hidden');
  pointsPillEl && pointsPillEl.classList.add('hidden');
  endOv.classList.remove('hidden');

  trackEvent('rami_snapshot_restored', {
    mode:      snap.mode,
    ageMs:     Date.now() - (snap.savedAt | 0),
    autoRetry: !state.isGuest,
  });
}

function pauseGame() {
  if (!state.running) return;
  state.paused = true;
  sheet.classList.remove('hidden');
}

function resumeGame() {
  state.paused = false;
  sheet.classList.add('hidden');
}

// Tear the round down and return to the day picker. No web button calls this —
// leaving the game is the native back button's job — but it stays as the
// handler for the native `game:exit` lifecycle event (see wallet.js).
function exitToMenu() {
  // Explicit exit drops any unclaimed snapshot — user chose to walk away.
  snapshot.clear();
  // Only the logged-in path holds a backend sessionId. Guests have nothing to abandon.
  if (state.sessionId) {
    api.abandonSession(state.sessionId).catch(() => {});
    state.sessionId = null;
  }
  sheet.classList.add('hidden');
  hud.classList.add('hidden');
  stonesPillEl && stonesPillEl.classList.add('hidden');
  pointsPillEl && pointsPillEl.classList.add('hidden');
  endOv.classList.add('hidden');
  transitionOv && transitionOv.classList.add('hidden');
  aboutOv && aboutOv.classList.add('hidden');
  state.running = false; state.paused = false; state.stone = null;
  // Guarantee the menu's day-picker is clickable whenever we show the start
  // overlay — the invariant "start overlay visible ⇒ cards enabled" is
  // enforced here, not inferred from happy-path callers.
  clearDayPickerLoading();
  startOv.classList.remove('hidden');
  sendToHost('game:exit', { game: 'rami' });
}

function advanceStage() {
  if (state.stageIdx < state.stages.length - 1) {
    const fromDef = state.stages[state.stageIdx].def;
    const nextIdx = state.stageIdx + 1;
    const toDef   = state.stages[nextIdx].def;

    // Pause gameplay while the transition card is up so a stray swipe
    // doesn't queue a throw before the next pillar is the active one.
    state.paused = true;
    Sfx.stage(); haptic([0, 30, 80, 30], 'success');
    showPillarTransition(fromDef, toDef, () => {
      state.stageIdx = nextIdx;
      state.stonesLeft = STONES_PER_JAMRAH;
      state.takbirCount = 0;
      buildHeap();
      state.paused = false;
      renderProgress();
    });
  } else {
    endGame();
  }
}

// ---- Main loop ----

let lastT = performance.now();

function loop(now) {
  const dt = Math.min(0.033, (now - lastT) / 1000);
  lastT = now;
  if (state.running && !state.paused) {
    if (state.totalThrows > 0) state.firstThrowDone = true;
    update(dt);
  }
  draw();
  requestAnimationFrame(loop);
}

// ---- Init ----

window.addEventListener('resize',            resize);
window.addEventListener('orientationchange', resize);
resize();

requestAnimationFrame(loop);
